import React, { useState, useEffect, useMemo } from 'react';
import { FaHeart, FaEye, FaEyeSlash, FaRegHeart, FaQuestionCircle, FaRegClock, FaRegStickyNote, FaTrash, FaEdit, FaSave, FaTimes, FaRegLightbulb, FaShareAlt, FaLink } from 'react-icons/fa';
import { Card, CardContent, useMediaQuery, Box, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Add CSS animations
const gradientAnimation = `
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
`;

const categories = [
  { key: 'vocab', label: 'مفردات' },
  { key: 'grammar', label: 'قواعد' },
  { key: 'sentence', label: 'إكمال الجمل' },
  { key: 'rephrase', label: 'إعادة الصياغة' },
  { key: 'reading', label: 'فهم المقروء' },
  { key: 'essay', label: 'كتابة مقال' },
];

const levels = [
  { key: 'easy', label: 'سهل' },
  { key: 'middle', label: 'متوسط' },
  { key: 'hard', label: 'صعب' },
];

// Helper to format time ago in Arabic
function timeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const diff = Math.floor((now - date) / 1000); // seconds
  if (diff < 60) return 'قبل لحظات';
  if (diff < 3600) return `قبل ${Math.floor(diff / 60)} دقيقة`;
  if (diff < 86400) return `قبل ${Math.floor(diff / 3600)} ساعة`;
  if (diff < 604800) return `قبل ${Math.floor(diff / 86400)} يوم`;
  if (diff < 2592000) return `قبل ${Math.floor(diff / 604800)} أسبوع`;
  if (diff < 31536000) return `قبل ${Math.floor(diff / 2592000)} شهر`;
  return 'قبل فترة طويلة';
}

// Utility function to create a seeded random number generator
function mulberry32(seed) {
  return function() {
    var t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  }
}

// Seeded shuffle using Fisher-Yates
function seededShuffle(array, seed) {
  const arr = [...array];
  const rand = mulberry32(seed);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// Referral Banner Component
function ReferralBanner({ onClose, onNavigate, referralStats, isMobile }) {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: isMobile ? '1rem' : '2rem'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 20,
        padding: isMobile ? '24px' : '32px',
        maxWidth: 500,
        width: '100%',
        color: 'white',
        position: 'relative',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        animation: 'slideInUp 0.3s ease-out'
      }}>
        <style>{`
          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
        
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '50%',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'white',
            fontSize: 18
          }}
        >
          ×
        </button>

        {/* Content */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎁</div>
          
          <h2 style={{ 
            fontSize: isMobile ? 20 : 24, 
            fontWeight: 'bold', 
            marginBottom: 12,
            margin: '0 0 12px 0'
          }}>
            استراحة قصيرة! 
          </h2>
          
          <p style={{ 
            fontSize: isMobile ? 15 : 17, 
            marginBottom: 20,
            lineHeight: 1.6,
            opacity: 0.95
          }}>
            هل تعلم أنه يمكنك الحصول على <strong>أسبوعين اشتراك مجاناً</strong> فقط بدعوة 3 أصدقاء؟
          </p>

          {/* Progress */}
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 12,
            padding: '16px',
            marginBottom: 20
          }}>
            <div style={{ fontSize: isMobile ? 14 : 16, marginBottom: 8 }}>
              تقدمك الحالي:
            </div>
            <div style={{ 
              fontSize: isMobile ? 24 : 32, 
              fontWeight: 'bold',
              marginBottom: 8
            }}>
              {referralStats?.totalReferrals || 0} / 3
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.3)',
              borderRadius: 8,
              height: 8,
              overflow: 'hidden',
              marginBottom: 8
            }}>
              <div style={{
                background: 'white',
                height: '100%',
                width: `${((referralStats?.totalReferrals || 0) / 3) * 100}%`,
                transition: 'width 0.3s ease',
                borderRadius: 8
              }}></div>
            </div>
            <div style={{ fontSize: isMobile ? 13 : 15, opacity: 0.9 }}>
              {3 - (referralStats?.totalReferrals || 0)} دعوات متبقية للحصول على المكافأة
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
            <button
              onClick={onNavigate}
              style={{
                flex: 1,
                background: 'white',
                color: '#667eea',
                border: 'none',
                padding: isMobile ? '12px 20px' : '14px 24px',
                borderRadius: 12,
                fontSize: isMobile ? 14 : 16,
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                transition: 'transform 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              احصل على رابط الدعوة
            </button>
            
            <button
              onClick={onClose}
              style={{
                flex: isMobile ? 1 : 'auto',
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                padding: isMobile ? '12px 20px' : '14px 24px',
                borderRadius: 12,
                fontSize: isMobile ? 14 : 16,
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'transform 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              لاحقاً
            </button>
          </div>

          <div style={{ 
            fontSize: isMobile ? 12 : 13, 
            marginTop: 16,
            opacity: 0.8
          }}>
            يظهر هذا التنبيه كل 10 أسئلة
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Practice() {
  const navigate = useNavigate();
  
  // Load saved preferences from localStorage
  const getSavedCategory = () => {
    try {
      const saved = localStorage.getItem('practiceSelectedCategory');
      return saved || 'sentence'; // Default to sentence completion
    } catch (error) {
      return 'sentence'; // Default to sentence completion
    }
  };

  const getSavedLevel = () => {
    try {
      const saved = localStorage.getItem('practiceSelectedLevel');
      return saved || 'easy';
    } catch (error) {
      return 'easy';
    }
  };

  const [selectedCategory, setSelectedCategory] = useState(getSavedCategory);
  const [selectedLevel, setSelectedLevel] = useState(getSavedLevel);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [flashcardMode, setFlashcardMode] = useState(false);
  const [showFlashcardAnswer, setShowFlashcardAnswer] = useState(false);

  // Vocabulary list flashcard mode states
  const [vocabDisplayMode, setVocabDisplayMode] = useState('list'); // 'list' or 'cards'
  const [currentVocabCardIndex, setCurrentVocabCardIndex] = useState(0);
  const [showVocabCardAnswer, setShowVocabCardAnswer] = useState(false);

  // Reading article vocabulary flashcard mode states
  const [articleVocabDisplayMode, setArticleVocabDisplayMode] = useState('list');
  const [currentArticleVocabCardIndex, setCurrentArticleVocabCardIndex] = useState(0);
  const [showArticleVocabCardAnswer, setShowArticleVocabCardAnswer] = useState(false);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userFavorites, setUserFavorites] = useState([]);
  const [user, setUser] = useState(null);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [showOnlyUnanswered, setShowOnlyUnanswered] = useState(false);
  const [questionStats, setQuestionStats] = useState([]);
  const [showStats, setShowStats] = useState(true);
  const [orderBy, setOrderBy] = useState('default');
  const [timerEnabled, setTimerEnabled] = useState(true);
  const [timer, setTimer] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const timerRef = React.useRef();
  const [notes, setNotes] = useState([]); // { question, text }
  const [noteEditMode, setNoteEditMode] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const feedbackRef = React.useRef();
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [showHint, setShowHint] = useState(false);
  
  // Referral system state
  const [referralStats, setReferralStats] = useState(null);
  const [showReferralBanner, setShowReferralBanner] = useState(false);
  const [referralBannerDismissed, setReferralBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem('referralBannerDismissed') === 'true';
    } catch {
      return false;
    }
  });
  
  // Question navigation state
  const [jumpToQuestion, setJumpToQuestion] = useState('');
  const [showJumpInput, setShowJumpInput] = useState(false);
  
  // Load filters collapsed state from localStorage
  useEffect(() => {
    const savedFiltersCollapsed = localStorage.getItem('filtersCollapsed');
    if (savedFiltersCollapsed !== null) {
      setFiltersCollapsed(savedFiltersCollapsed === 'true');
    }
  }, []);
  
  // Save filters collapsed state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('filtersCollapsed', filtersCollapsed.toString());
  }, [filtersCollapsed]);
  const [showTimer, setShowTimer] = useState(true);

  const [readingArticle, setReadingArticle] = useState(null);
  const [readingAnswers, setReadingAnswers] = useState([]);
  const [readingFeedback, setReadingFeedback] = useState([]);
  const [readingShowHint, setReadingShowHint] = useState([]);
  const [readingArticles, setReadingArticles] = useState([]);
  const [currentReadingArticleIndex, setCurrentReadingArticleIndex] = useState(0);
  const [readingTimers, setReadingTimers] = useState([]); // array of seconds per article
  const [readingTimerActive, setReadingTimerActive] = useState(false);
  const readingTimerRef = React.useRef();
  const [readingArticleAnswered, setReadingArticleAnswered] = useState([]); // array of booleans per article
  const [userFavoriteArticles, setUserFavoriteArticles] = useState([]); // array of article _id strings
  const [readingArticleLastAttempt, setReadingArticleLastAttempt] = useState([]); // array of Date strings per article
  const [articleStats, setArticleStats] = useState([]); // array of { article, lastAttempt }
  const [articleQuestionStats, setArticleQuestionStats] = useState([]); // array of stats for article sub-questions

  // Essay state
  const [essayAnswers, setEssayAnswers] = useState([]); // array of essay answers
  const [essayFeedback, setEssayFeedback] = useState([]); // array of essay feedback
  const [essaySubmitted, setEssaySubmitted] = useState([]); // array of boolean for submitted essays
  const [essayReviewLoading, setEssayReviewLoading] = useState(false); // loading state for essay review

  const [premiumModal, setPremiumModal] = useState(false);
  
  // Get user type for different messaging
  const getUserType = (user) => {
    if (!user) return 'none';
    
    const isPremiumActive = user.premium && user.premiumUntil && new Date(user.premiumUntil) > new Date();
    
    if (!isPremiumActive) return 'non-premium';
    
    if (user.hasReceivedFreeTrial) {
      return 'free-trial';
    } else {
      return 'full-premium';
    }
  };
  
  const userType = getUserType(user);
  
  const getPremiumMessage = () => {
    switch (userType) {
      case 'free-trial':
        return 'هذه الميزة متاحة فقط للمشتركين الكاملين. اشترك الآن للاستفادة من جميع الميزات.';
      case 'non-premium':
        return 'هذه الميزة متاحة فقط للمشتركين. اشترك الآن للاستفادة من جميع الميزات.';
      default:
        return 'هذه الميزة متاحة فقط للمشتركين. اشترك الآن للاستفادة من جميع الميزات.';
    }
  };
  
  const premiumMessage = getPremiumMessage();
  
  // User type variables - define early to avoid initialization errors
  const isPremiumActive = user && user.premium && user.premiumUntil && new Date(user.premiumUntil) > new Date();
  const isAdmin = user && user.role === 'admin';
  const isFreeTrial = user && user.hasReceivedFreeTrial && isPremiumActive;
  const isNonPremium = user && !isPremiumActive && !isAdmin;

  const isMobile = useMediaQuery('(max-width:600px)');
  const [totalCount, setTotalCount] = useState(0);
  const [readingArticlesTotalCount, setReadingArticlesTotalCount] = useState(0);

  // Save selected category to localStorage when it changes
  useEffect(() => {
    if (selectedCategory) {
      try {
        localStorage.setItem('practiceSelectedCategory', selectedCategory);
      } catch (error) {
        console.error('Failed to save selected category to localStorage:', error);
      }
    }
  }, [selectedCategory]);

  // Save selected level to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('practiceSelectedLevel', selectedLevel);
    } catch (error) {
      console.error('Failed to save selected level to localStorage:', error);
    }
  }, [selectedLevel]);

  // Fetch referral stats
  useEffect(() => {
    const fetchReferralStats = async () => {
      try {
        const response = await fetch((process.env.REACT_APP_API_URL || '') + '/api/user/referral-stats', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setReferralStats(data);
        }
      } catch (error) {
        console.error('Error fetching referral stats:', error);
      }
    };

    if (user) {
      fetchReferralStats();
    }
  }, [user]);

  // Show referral banner every 10 questions
  useEffect(() => {
    if (currentIndex > 0 && currentIndex % 10 === 0 && !referralBannerDismissed && referralStats && referralStats.totalReferrals < 3) {
      setShowReferralBanner(true);
    }
  }, [currentIndex, referralBannerDismissed, referralStats]);

  useEffect(() => {
    if (selectedCategory === 'sentence' || selectedCategory === 'rephrase' || selectedCategory === 'vocab' || selectedCategory === 'essay') {
      setLoading(true);
      setError(null);
      fetch(process.env.REACT_APP_API_URL + `/api/practice-questions?type=${selectedCategory}&level=${selectedLevel}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          setQuestions(data.questions || []);
          setTotalCount(data.totalCount || 0);
          setCurrentIndex(0);
          setSelectedAnswer(null);
          setShowFeedback(false);
          setShowFlashcardAnswer(false);
        })
        .catch(() => setError('حدث خطأ أثناء جلب الأسئلة'))
        .finally(() => setLoading(false));
    }
  }, [selectedCategory, selectedLevel]);

  useEffect(() => {
    fetch(process.env.REACT_APP_API_URL + '/api/user', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setUser(data.user);
        if (data.user) {
          fetch(process.env.REACT_APP_API_URL + '/api/user/favorites', { credentials: 'include' })
            .then(res => res.json())
            .then(data => setUserFavorites(data.favorites ? data.favorites.map(q => q._id.toString()) : []));
          fetch(process.env.REACT_APP_API_URL + '/api/user/question-stats', { credentials: 'include' })
            .then(res => res.json())
            .then(data => setQuestionStats(data.stats || []));

        }
      });
  }, []);

  // Add state for filteredQuestions
  const [filteredQuestionsState, setFilteredQuestionsState] = useState([]);

  // Update filteredQuestionsState only when questions, userFavorites, showOnlyFavorites, showOnlyUnanswered, or user change (remove questionStats from deps)
  useEffect(() => {
    let filtered = questions;
    if (showOnlyFavorites && user) {
      filtered = filtered.filter(q => userFavorites.includes(q._id && q._id.toString()));
    }
    if (showOnlyUnanswered && user) {
      filtered = filtered.filter(q => {
        const stat = questionStats.find(qs => qs.question && (qs.question._id ? qs.question._id.toString() : qs.question.toString()) === (q._id && q._id.toString()));
        return !stat || (stat.correct === 0 && stat.wrong === 0);
      });
    }
    setFilteredQuestionsState(filtered);
  }, [questions, userFavorites, showOnlyFavorites, showOnlyUnanswered, user]);

  // Use filteredQuestionsState instead of filteredQuestions in the rest of the component
  let filteredQuestions = filteredQuestionsState;

  const getStatsForQuestion = (qid) => {
    const stat = questionStats.find(qs => qs.question && (qs.question._id ? qs.question._id.toString() : qs.question.toString()) === qid);
    return stat ? { correct: stat.correct, wrong: stat.wrong, lastAttempt: stat.lastAttempt } : { correct: 0, wrong: 0, lastAttempt: null };
  };

  // Sorting logic
  let sortedQuestions = [...filteredQuestions];
  if (orderBy === 'wrong') {
    sortedQuestions.sort((a, b) => {
      const aStat = getStatsForQuestion(a._id && a._id.toString());
      const bStat = getStatsForQuestion(b._id && b._id.toString());
      return (bStat.wrong || 0) - (aStat.wrong || 0);
    });
  } else if (orderBy === 'lastAttempt') {
    sortedQuestions.sort((a, b) => {
      const aStat = getStatsForQuestion(a._id && a._id.toString());
      const bStat = getStatsForQuestion(b._id && b._id.toString());
      const aTime = aStat.lastAttempt ? new Date(aStat.lastAttempt).getTime() : 0;
      const bTime = bStat.lastAttempt ? new Date(bStat.lastAttempt).getTime() : 0;
      return aTime - bTime;
    });
  } else if (orderBy === 'random') {
    // Fisher-Yates shuffle (stable for the session)
    if (!window._randomOrder || window._randomOrder.length !== sortedQuestions.length) {
      window._randomOrder = sortedQuestions.map((_, i) => i);
      for (let i = window._randomOrder.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [window._randomOrder[i], window._randomOrder[j]] = [window._randomOrder[j], window._randomOrder[i]];
      }
    }
    sortedQuestions = window._randomOrder.map(i => sortedQuestions[i]);
  }

  // Update currentIndex when showOnlyFavorites changes, but keep the current question if possible
  useEffect(() => {
    if (showOnlyFavorites) {
      // Try to keep the current question visible
      const currentQ = sortedQuestions[currentIndex];
      const favIds = userFavorites;
      const favIndex = displayQuestions.findIndex(q => favIds.includes(q._id && q._id.toString()) && q._id === (currentQ && currentQ._id));
      if (favIndex !== -1) {
        setCurrentIndex(favIndex);
      } else {
        setCurrentIndex(0);
      }
    }
  }, [showOnlyFavorites]);

  const isFavorite = (selectedCategory === 'sentence' || selectedCategory === 'rephrase' || selectedCategory === 'vocab') && sortedQuestions[currentIndex] && userFavorites.includes(sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString());

  const toggleFavorite = async () => {
    if (!user || !sortedQuestions[currentIndex]) return;
    const qid = sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString();
    if (isFavorite) {
      await fetch(process.env.REACT_APP_API_URL + `/api/user/favorites/${qid}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setUserFavorites(favs => favs.filter(id => id !== qid));
    } else {
      await fetch(process.env.REACT_APP_API_URL + '/api/user/favorites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ questionId: qid }),
      });
      setUserFavorites(favs => [...favs, qid]);
    }
  };

  const shareQuestion = () => {
    if (!sortedQuestions[currentIndex]) return;
    const qid = sortedQuestions[currentIndex]._id;
    const url = `${window.location.origin}/practice/question/${qid}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'سؤال من ياعيل ماستر',
        text: 'شاهد هذا السؤال من منصة ياعيل ماستر',
        url: url
      }).catch(() => {
        // Fallback if share fails
        navigator.clipboard.writeText(url);
        alert('تم نسخ الرابط!');
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('تم نسخ الرابط!');
    }
  };

  const viewQuestion = () => {
    if (!sortedQuestions[currentIndex]) return;
    const qid = sortedQuestions[currentIndex]._id;
    navigate(`/practice/question/${qid}`);
  };

  const handleAnswer = async (choice) => {
    if (showFeedback) return;
    setSelectedAnswer(choice);
    setShowFeedback(true);
    if (user && sortedQuestions[currentIndex]) {
      const qid = sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString();
      const result = choice === sortedQuestions[currentIndex].correct ? 'correct' : 'wrong';
      await fetch(process.env.REACT_APP_API_URL + '/api/user/question-stats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ questionId: qid, result }),
      });
      setQuestionStats(prevStats => {
        const idx = prevStats.findIndex(qs => (qs.question && (qs.question._id ? qs.question._id.toString() : qs.question.toString())) === qid);
        if (idx !== -1) {
          const updated = [...prevStats];
          updated[idx] = {
            ...updated[idx],
            correct: (updated[idx].correct || 0) + (result === 'correct' ? 1 : 0),
            wrong: (updated[idx].wrong || 0) + (result === 'wrong' ? 1 : 0),
            lastAttempt: new Date().toISOString()
          };
          return updated;
        } else {
          return [...prevStats, { 
            question: { _id: qid }, 
            correct: result === 'correct' ? 1 : 0, 
            wrong: result === 'wrong' ? 1 : 0,
            lastAttempt: new Date().toISOString()
          }];
        }
      });
    }
  };

  const handleNext = () => {
    setSelectedAnswer(null);
    setShowFeedback(false);
    setShowFlashcardAnswer(false);
    // Reset vocabulary flashcard states
    setVocabDisplayMode('list');
    setCurrentVocabCardIndex(0);
    setShowVocabCardAnswer(false);
    setCurrentIndex((prev) => Math.min(prev + 1, sortedQuestions.length - 1));
  };

  // Question navigation handlers
  const handleJumpToQuestion = () => {
    const questionNumber = parseInt(jumpToQuestion);
    if (questionNumber >= 1 && questionNumber <= sortedQuestions.length) {
      setCurrentIndex(questionNumber - 1); // Convert to 0-based index
      setJumpToQuestion('');
      setShowJumpInput(false);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setShowFlashcardAnswer(false);
      // Reset vocabulary flashcard states
      setVocabDisplayMode('list');
      setCurrentVocabCardIndex(0);
      setShowVocabCardAnswer(false);
    }
  };

  const handleJumpKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleJumpToQuestion();
    } else if (e.key === 'Escape') {
      setShowJumpInput(false);
      setJumpToQuestion('');
    }
  };

  // Check if navigation should be shown (allow category and level filters, but not favorites/unanswered/custom ordering)
  const shouldShowNavigation = !showOnlyFavorites && 
    !showOnlyUnanswered && 
    orderBy === 'default' &&
    sortedQuestions.length > 10; // Only show for larger question sets

  const handlePrev = () => {
    setSelectedAnswer(null);
    setShowFeedback(false);
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  // On mount, load showStats from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('showStats');
    if (saved !== null) setShowStats(saved === 'true');
  }, []);

  // Start timer if enabled, not answered, and question exists (sentence/rephrase)
  useEffect(() => {
    if ((selectedCategory === 'sentence' || selectedCategory === 'rephrase' || selectedCategory === 'vocab') && timerEnabled && !showFeedback && sortedQuestions.length > 0) {
      if (!timerActive) {
        setTimer(0);
        setTimerActive(true);
      }
    } else {
      setTimerActive(false);
    }
    // eslint-disable-next-line
  }, [timerEnabled, currentIndex, sortedQuestions, showFeedback, selectedCategory]);

  useEffect(() => {
    if (timerActive) {
      timerRef.current = setInterval(() => {
        setTimer(t => t + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [timerActive]);

    // Fetch notes on mount if user is logged in
  useEffect(() => {
    if (user) {
      fetch(process.env.REACT_APP_API_URL + '/api/user/notes', { credentials: 'include' })
        .then(res => res.json())
        .then(data => setNotes(data.notes || []));
    }
  }, [user]);

  // Helper to get note for a question
  const getNoteForQuestion = (qid) => {
    return notes.find(n => n.question && (n.question._id ? n.question._id.toString() : n.question.toString()) === qid);
  };

  // Add or update note
  const saveNote = async (qid, text) => {
    await fetch(process.env.REACT_APP_API_URL + '/api/user/notes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ questionId: qid, text }),
    });
    setNotes(prev => {
      const idx = prev.findIndex(n => (n.question && (n.question._id ? n.question._id.toString() : n.question.toString())) === qid);
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], text };
        return updated;
      } else {
        return [...prev, { question: { _id: qid }, text }];
      }
    });
    setNoteEditMode(false);
  };

  // Delete note
  const deleteNote = async (qid) => {
    await fetch(process.env.REACT_APP_API_URL + `/api/user/notes/${qid}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    setNotes(prev => prev.filter(n => (n.question && (n.question._id ? n.question._id.toString() : n.question.toString())) !== qid));
    setNoteEditMode(false);
  };

  // Handle essay review submission
  const handleEssayReview = async () => {
    const currentEssay = essayAnswers[currentIndex];
    const currentQuestion = sortedQuestions[currentIndex];
    
    if (!currentEssay || !currentQuestion) {
      return;
    }

    // Check word count
    const wordCount = currentEssay.split(/\s+/).filter(word => word.length > 0).length;
    const minWords = 120;
    const maxWords = 160;
    
    if (wordCount < minWords || wordCount > maxWords) {
      alert(`يجب أن يحتوي المقال على عدد كلمات بين ${minWords} و ${maxWords} كلمة. عدد الكلمات الحالي: ${wordCount}`);
      return;
    }

    // Check Hebrew word percentage
    const hebrewRegex = /[\u0590-\u05FF\uFB1D-\uFB4F]/; // Hebrew Unicode range
    const words = currentEssay.split(/\s+/).filter(word => word.length > 0);
    const hebrewWords = words.filter(word => hebrewRegex.test(word));
    const hebrewPercentage = (hebrewWords.length / words.length) * 100;
    const minHebrewPercentage = 90;
    
    if (hebrewPercentage < minHebrewPercentage) {
      alert(`يجب أن تكون 90% من الكلمات على الأقل باللغة العبرية. النسبة الحالية: ${hebrewPercentage.toFixed(1)}%`);
      return;
    }

    setEssayReviewLoading(true);
    
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/api/essay-review', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          essay: currentEssay,
          topic: currentQuestion.topic,
          questionId: currentQuestion._id
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Update essay feedback
        const newFeedback = [...essayFeedback];
        newFeedback[currentIndex] = data.review;
        setEssayFeedback(newFeedback);
        
        // Mark as submitted
        const newSubmitted = [...essaySubmitted];
        newSubmitted[currentIndex] = true;
        setEssaySubmitted(newSubmitted);
        
        // Save essay answer to stats
        if (user && currentQuestion) {
          const qid = currentQuestion._id && currentQuestion._id.toString();
          fetch(process.env.REACT_APP_API_URL + '/api/user/question-stats', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ questionId: qid, result: 'correct' }),
          });
        }
      } else if (response.status === 403 && data.error === 'Premium feature') {
        // Show premium modal
        setPremiumModal(true);
      } else if (response.status === 403 && data.error === 'Free trial restriction') {
        // Show free trial restriction message
        alert(data.message || 'ميزة مراجعة المقالات غير متاحة للمستخدمين في الفترة التجريبية المجانية. اشترك الآن للاستفادة من هذه الميزة.');
      } else if (response.status === 429 && data.error === 'Daily limit exceeded') {
        // Show daily limit message
        alert(data.message || 'لقد وصلت إلى الحد الأقصى اليومي لمراجعة المقالات (10 مراجعات). يمكنك المحاولة مرة أخرى غداً.');
      } else {
        console.error('Essay review failed:', data.error);
      }
    } catch (error) {
      console.error('Essay review error:', error);
    } finally {
      setEssayReviewLoading(false);
    }
  };

  useEffect(() => {
    if (showFeedback && feedbackRef.current) {
      feedbackRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showFeedback]);

  // Reset hint when question changes or feedback is shown
  useEffect(() => { setShowHint(false); }, [currentIndex, showFeedback]);

  // Fetch reading article when selected
  useEffect(() => {
    if (selectedCategory === 'reading') {
      setLoading(true);
      setError(null);
      fetch(process.env.REACT_APP_API_URL + `/api/practice-questions?type=reading&level=${selectedLevel}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          const article = data.questions && data.questions[0];
          setReadingArticle(article || null);
          setReadingAnswers(article ? Array(article.questions.length).fill(null) : []);
          setReadingFeedback(article ? Array(article.questions.length).fill(false) : []);
          setReadingShowHint(article ? Array(article.questions.length).fill(false) : []);
          setCurrentIndex(0);
          setSelectedAnswer(null);
          setShowFeedback(false);
        })
        .catch(() => setError('حدث خطأ أثناء جلب الأسئلة'))
        .finally(() => setLoading(false));
    }
  }, [selectedCategory, selectedLevel]);

  // Fetch all reading articles when selected
  useEffect(() => {
    if (selectedCategory === 'reading') {
      setLoading(true);
      setError(null);
      fetch(process.env.REACT_APP_API_URL + `/api/practice-questions?type=reading&level=${selectedLevel}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          const articles = data.questions || [];
          setReadingArticles(articles);
          setReadingArticlesTotalCount(data.totalCount || 0);
          setCurrentReadingArticleIndex(0);
          const article = articles[0];
          setReadingArticle(article || null);
          setReadingAnswers(article ? Array(article.questions.length).fill(null) : []);
          setReadingFeedback(article ? Array(article.questions.length).fill(false) : []);
          setReadingShowHint(article ? Array(article.questions.length).fill(false) : []);
          setCurrentIndex(0);
          setSelectedAnswer(null);
          setShowFeedback(false);
        })
        .catch(() => setError('حدث خطأ أثناء جلب الأسئلة'))
        .finally(() => setLoading(false));
    }
  }, [selectedCategory, selectedLevel]);

  // For reading, filter readingArticles for unanswered filter
  let filteredReadingArticles = readingArticles;
  if (showOnlyUnanswered && user && selectedCategory === 'reading') {
    filteredReadingArticles = readingArticles.filter(article => {
      if (!article.questions || article.questions.length === 0) return false;
      return article.questions.some((q, idx) => {
        const stat = articleQuestionStats.find(s =>
          s.article && (s.article._id ? s.article._id.toString() : s.article.toString()) === (article._id && article._id.toString()) &&
          s.questionIndex === idx
        );
        return !stat || (stat.correct === 0 && stat.wrong === 0);
      });
    });
  }
  if (showOnlyFavorites && user && selectedCategory === 'reading') {
    filteredReadingArticles = readingArticles.filter(article =>
      userFavoriteArticles.includes(article._id && article._id.toString())
    );
  }

  // Sorting logic for reading articles
  let sortedReadingArticles = [...filteredReadingArticles];
  if (selectedCategory === 'reading' && orderBy === 'wrong') {
    sortedReadingArticles.sort((a, b) => {
      const aStats = articleQuestionStats.filter(s =>
        s.article && (s.article._id ? s.article._id.toString() : s.article.toString()) === (a._id && a._id.toString())
      );
      const bStats = articleQuestionStats.filter(s =>
        s.article && (s.article._id ? s.article._id.toString() : s.article.toString()) === (b._id && b._id.toString())
      );
      const aTotalWrong = aStats.reduce((sum, stat) => sum + (stat.wrong || 0), 0);
      const bTotalWrong = bStats.reduce((sum, stat) => sum + (stat.wrong || 0), 0);
      return bTotalWrong - aTotalWrong;
    });
  } else if (selectedCategory === 'reading' && orderBy === 'lastAttempt') {
    sortedReadingArticles.sort((a, b) => {
      const aStat = articleStats.find(s =>
        s.article && (s.article._id ? s.article._id.toString() : s.article.toString()) === (a._id && a._id.toString())
      );
      const bStat = articleStats.find(s =>
        s.article && (s.article._id ? s.article._id.toString() : s.article.toString()) === (b._id && b._id.toString())
      );
      const aTime = aStat && aStat.lastAttempt ? new Date(aStat.lastAttempt).getTime() : 0;
      const bTime = bStat && bStat.lastAttempt ? new Date(bStat.lastAttempt).getTime() : 0;
      return aTime - bTime;
    });
  } else if (selectedCategory === 'reading' && orderBy === 'random') {
    // Fisher-Yates shuffle for reading articles (stable for the session)
    if (!window._randomReadingOrder || window._randomReadingOrder.length !== sortedReadingArticles.length) {
      window._randomReadingOrder = sortedReadingArticles.map((_, i) => i);
      for (let i = window._randomReadingOrder.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [window._randomReadingOrder[i], window._randomReadingOrder[j]] = [window._randomReadingOrder[j], window._randomReadingOrder[i]];
      }
    }
    sortedReadingArticles = window._randomReadingOrder.map(i => sortedReadingArticles[i]);
  }

  // Update filteredReadingArticles to use the sorted version
  filteredReadingArticles = sortedReadingArticles;
  
  // Apply limits for free trial users
  if (isFreeTrial && selectedCategory === 'reading') {
    filteredReadingArticles = filteredReadingArticles.slice(0, 100);
  }

  // Handler to go to next/prev article
  const handleNextArticle = () => {
    // Check premium restriction for non-premium users
    if (user && !user.premium && user.role !== 'admin' && currentReadingArticleIndex >= 4) {
      setPremiumModal(true);
      return;
    }
    
    // Check free trial restriction (limit to 100 articles)
    if (isFreeTrial && currentReadingArticleIndex >= 99) {
      setPremiumModal(true);
      return;
    }
    
    if (currentReadingArticleIndex < filteredReadingArticles.length - 1) {
      const nextIdx = currentReadingArticleIndex + 1;
      setCurrentReadingArticleIndex(nextIdx);
      const article = filteredReadingArticles[nextIdx];
      setReadingArticle(article || null);
      setReadingAnswers(article ? Array(article.questions.length).fill(null) : []);
      setReadingFeedback(article ? Array(article.questions.length).fill(false) : []);
      setReadingShowHint(article ? Array(article.questions.length).fill(false) : []);
      // Reset article vocabulary flashcard states
      setArticleVocabDisplayMode('list');
      setCurrentArticleVocabCardIndex(0);
      setShowArticleVocabCardAnswer(false);
    }
  };
  const handlePrevArticle = () => {
    if (currentReadingArticleIndex > 0) {
      const prevIdx = currentReadingArticleIndex - 1;
      setCurrentReadingArticleIndex(prevIdx);
      const article = filteredReadingArticles[prevIdx];
      setReadingArticle(article || null);
      setReadingAnswers(article ? Array(article.questions.length).fill(null) : []);
      setReadingFeedback(article ? Array(article.questions.length).fill(false) : []);
      setReadingShowHint(article ? Array(article.questions.length).fill(false) : []);
      // Reset article vocabulary flashcard states
      setArticleVocabDisplayMode('list');
      setCurrentArticleVocabCardIndex(0);
      setShowArticleVocabCardAnswer(false);
    }
  };

  // Reset timer when question, article, or type changes
  useEffect(() => {
    setTimer(0);
    setTimerActive(false);
  }, [currentIndex, selectedCategory, readingArticle]);

  // When readingArticles changes, initialize readingTimers to zeros
  useEffect(() => {
    if (selectedCategory === 'reading' && filteredReadingArticles.length > 0) {
      if (!readingTimerActive) {
        setReadingTimers(Array(filteredReadingArticles.length).fill(0));
        setReadingTimerActive(true);
      }
    }
  }, [filteredReadingArticles.length, selectedCategory]);

  // Timer for reading articles
  useEffect(() => {
    if (selectedCategory === 'reading' && timerEnabled && filteredReadingArticles.length > 0) {
      setReadingTimerActive(true);
    } else {
      setReadingTimerActive(false);
    }
  }, [timerEnabled, selectedCategory, currentReadingArticleIndex, filteredReadingArticles.length]);

  useEffect(() => {
    if (readingTimerActive) {
      readingTimerRef.current = setInterval(() => {
        setReadingTimers(timers => {
          const arr = [...timers];
          arr[currentReadingArticleIndex] = (arr[currentReadingArticleIndex] || 0) + 1;
          return arr;
        });
      }, 1000);
    } else {
      clearInterval(readingTimerRef.current);
    }
    return () => clearInterval(readingTimerRef.current);
  }, [readingTimerActive, currentReadingArticleIndex]);

  // Reset reading timer when article changes
  useEffect(() => {
    if (selectedCategory === 'reading' && filteredReadingArticles.length > 0) {
      setReadingTimers(timers => {        
        const arr = [...timers];
        arr[currentReadingArticleIndex] = 0;
        return arr;
      });
      setReadingTimerActive(false);
    }
  }, [currentReadingArticleIndex, selectedCategory, filteredReadingArticles.length]);

  // Update readingArticleAnswered when answers change
  useEffect(() => {
    if (selectedCategory === 'reading' && filteredReadingArticles.length > 0) {
      setReadingArticleAnswered(prev => {
        const arr = [...prev];
        arr[currentReadingArticleIndex] = readingAnswers.length > 0 && readingAnswers.every(ans => ans !== null);
        // Ensure array is correct length
        if (arr.length !== filteredReadingArticles.length) {
          for (let i = arr.length; i < filteredReadingArticles.length; i++) arr[i] = false;
        }
        return arr;
      });
    }
  }, [readingAnswers, currentReadingArticleIndex, filteredReadingArticles.length, selectedCategory]);

  // Stop the reading timer if all questions in the current article are answered
  useEffect(() => {
    if (
      selectedCategory === 'reading' &&
      readingArticleAnswered[currentReadingArticleIndex]
    ) {
      setReadingTimerActive(false);
    }
  }, [readingArticleAnswered, currentReadingArticleIndex, selectedCategory]);

  // Add favorite toggle logic for reading articles
  const isArticleFavorite = selectedCategory === 'reading' && filteredReadingArticles[currentReadingArticleIndex] && userFavoriteArticles.includes(filteredReadingArticles[currentReadingArticleIndex]._id && filteredReadingArticles[currentReadingArticleIndex]._id.toString());
  const toggleArticleFavorite = async () => {
    if (!user || !filteredReadingArticles[currentReadingArticleIndex]) return;
    const aid = filteredReadingArticles[currentReadingArticleIndex]._id && filteredReadingArticles[currentReadingArticleIndex]._id.toString();
    if (isArticleFavorite) {
      // Remove from favorites (implement backend route as needed)
      await fetch(process.env.REACT_APP_API_URL + `/api/user/favorite-articles/${aid}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setUserFavoriteArticles(favs => favs.filter(id => id !== aid));
    } else {
      await fetch(process.env.REACT_APP_API_URL + '/api/user/favorite-articles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ articleId: aid }),
      });
      setUserFavoriteArticles(favs => [...favs, aid]);
    }
  };

  // Fetch user favorite articles on mount if user is logged in
  useEffect(() => {
    if (user) {
      fetch(process.env.REACT_APP_API_URL + '/api/user/favorite-articles', { credentials: 'include' })
        .then(res => res.json())
        .then(data => setUserFavoriteArticles(data.favorites ? data.favorites.map(a => a._id.toString()) : []));
    }
  }, [user]);

  // Fetch article stats on mount if user is logged in
  useEffect(() => {
    if (user) {
      fetch(process.env.REACT_APP_API_URL + '/api/user/article-stats', { credentials: 'include' })
        .then(res => res.json())
        .then(data => setArticleStats(data.stats || []));
    }
  }, [user]);

  // Fetch article question stats on mount if user is logged in
  useEffect(() => {
    if (user) {
      fetch(process.env.REACT_APP_API_URL + '/api/user/article-question-stats', { credentials: 'include' })
        .then(res => res.json())
        .then(data => setArticleQuestionStats(data.stats || []));
    }
  }, [user]);

  // When an article is completed, update lastAttempt in backend
  useEffect(() => {
    if (
      user &&
      selectedCategory === 'reading' &&
      filteredReadingArticles.length > 0 &&
      readingArticleAnswered[currentReadingArticleIndex]
    ) {
      const articleId = filteredReadingArticles[currentReadingArticleIndex]._id && filteredReadingArticles[currentReadingArticleIndex]._id.toString();
      fetch(process.env.REACT_APP_API_URL + '/api/user/article-stats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ articleId }),
      })
        .then(res => res.json())
        .then(() => {
          // Refresh stats after update
          fetch(process.env.REACT_APP_API_URL + '/api/user/article-stats', { credentials: 'include' })
            .then(res => res.json())
            .then(data => setArticleStats(data.stats || []));
        });
    }
  }, [readingArticleAnswered, currentReadingArticleIndex, selectedCategory, filteredReadingArticles, user]);

  // Helper to get last attempt for an article
  const getLastAttemptForArticle = (articleId) => {
    const stat = articleStats.find(a => a.article && (a.article._id ? a.article._id.toString() : a.article.toString()) === articleId);
    return stat && stat.lastAttempt ? stat.lastAttempt : null;
  };

  // Helper to get stats for a sub-question
  const getStatsForArticleQuestion = (articleId, questionIndex) => {
    const stat = articleQuestionStats.find(s =>
      s.article && (s.article._id ? s.article._id.toString() : s.article.toString()) === articleId &&
      s.questionIndex === questionIndex
    );
    return stat ? stat : { correct: 0, wrong: 0, lastAttempt: null };
  };

  // When a user answers a sub-question in a reading article, update stats in backend
  const handleReadingAnswer = (choice, idx) => {
    if (readingFeedback[idx]) return;
    const newAnswers = [...readingAnswers];
    newAnswers[idx] = choice;
    setReadingAnswers(newAnswers);
    const newFeedback = [...readingFeedback];
    newFeedback[idx] = true;
    setReadingFeedback(newFeedback);
    if (user && filteredReadingArticles[currentReadingArticleIndex]) {
      const articleId = filteredReadingArticles[currentReadingArticleIndex]._id && filteredReadingArticles[currentReadingArticleIndex]._id.toString();
      const result = choice === filteredReadingArticle.questions[idx].correct ? 'correct' : 'wrong';
      fetch(process.env.REACT_APP_API_URL + '/api/user/article-question-stats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ articleId, questionIndex: idx, result }),
      })
        .then(res => res.json())
        .then(() => {
          // Refresh stats after update
          fetch(process.env.REACT_APP_API_URL + '/api/user/article-question-stats', { credentials: 'include' })
            .then(res => res.json())
            .then(data => setArticleQuestionStats(data.stats || []));
        });
    }
  };

  const filteredReadingArticle = filteredReadingArticles[currentReadingArticleIndex];

  // Modified unanswered button handler
  const handleShowOnlyUnanswered = () => {
    if (user && !user.premium && user.role !== 'admin') {
      setPremiumModal(true);
      return;
    }
    setShowOnlyUnanswered(u => {
      const enabling = !u;
      if (enabling) {
        setShowFeedback(false);
        setSelectedAnswer(null);
        setCurrentIndex(0);
      } else {
        setCurrentIndex(0);
      }
      return !u;
    });
  };
  // Modified order change handler
  const handleOrderChange = (e) => {
    if (user && !user.premium && user.role !== 'admin') {
      setPremiumModal(true);
      return;
    }
    setOrderBy(e.target.value);
  };
  // Modified next question handler
  const handleNextPremium = () => {
    if (user && !user.premium && user.role !== 'admin' && currentIndex >= 4) {
      setPremiumModal(true);
      return;
    }
    setSelectedAnswer(null);
    setShowFeedback(false);
    setShowFlashcardAnswer(false);
    // Reset vocabulary flashcard states
    setVocabDisplayMode('list');
    setCurrentVocabCardIndex(0);
    setShowVocabCardAnswer(false);
    setCurrentIndex((prev) => Math.min(prev + 1, sortedQuestions.length - 1));
  };

  // Add a function to delete a question
  const handleDeleteQuestion = async (qid) => {
    if (!window.confirm('هل أنت متأكد أنك تريد حذف هذا السؤال؟')) return;
    await fetch(process.env.REACT_APP_API_URL + `/api/admin/practice-questions/${qid}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    setQuestions(prev => prev.filter(q => (q._id && q._id.toString()) !== qid));
  };

  useEffect(() => {
    if (
      selectedCategory === 'reading' &&
      currentReadingArticleIndex >= filteredReadingArticles.length
    ) {
      setCurrentReadingArticleIndex(0);
    }
  }, [filteredReadingArticles.length, selectedCategory]);

  // For regular questions, shuffle choices when question changes
  const shuffledChoices = useMemo(() => {
    const q = sortedQuestions[currentIndex];
    if (!q || !q.choices) return [];
    // Use question _id as seed (convert to number)
    let seed = 0;
    if (q._id) {
      for (let i = 0; i < q._id.length; i++) seed += q._id.charCodeAt(i);
    } else {
      seed = currentIndex + 1;
    }
    return seededShuffle(q.choices, seed);
  }, [sortedQuestions, currentIndex]);

  // For reading articles, shuffle choices for each sub-question
  const shuffledReadingChoices = useMemo(() => {
    if (
      selectedCategory !== 'reading' ||
      !filteredReadingArticles[currentReadingArticleIndex] ||
      !filteredReadingArticles[currentReadingArticleIndex].questions
    ) return [];
    return filteredReadingArticles[currentReadingArticleIndex].questions.map((q, idx) => {
      let seed = 0;
      if (filteredReadingArticles[currentReadingArticleIndex]._id) {
        for (let i = 0; i < filteredReadingArticles[currentReadingArticleIndex]._id.length; i++) seed += filteredReadingArticles[currentReadingArticleIndex]._id.charCodeAt(i);
        seed += idx * 9973; // add sub-question index for uniqueness
      } else {
        seed = idx + 1;
      }
      return q.choices ? seededShuffle(q.choices, seed) : [];
    });
  }, [filteredReadingArticles, currentReadingArticleIndex, selectedCategory]);

  if (premiumModal) {
    return (
      <div style={{ direction: 'rtl', padding: '2rem', maxWidth: 800, margin: '0 auto', position: 'relative' }}>
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.25)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff',
            padding: '2.5rem 2rem',
            borderRadius: 12,
            boxShadow: '0 4px 32px rgba(0,0,0,0.18)',
            textAlign: 'center',
            minWidth: 320,
            fontSize: 22,
            color: '#1a237e',
            fontWeight: 'bold',
          }}>
            {premiumMessage}
            <br />
            <a href="/register" style={{ margin: '24px 8px 0 8px', background: '#22c55e', color: '#fff', borderRadius: 8, padding: '10px 32px', fontWeight: 'bold', fontSize: 18, textDecoration: 'none', display: 'inline-block' }}>اشترك الآن</a>
            <button onClick={() => setPremiumModal(false)} style={{ margin: '24px 8px 0 8px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 32px', fontWeight: 'bold', fontSize: 18, cursor: 'pointer', display: 'inline-block' }}>حسنًا</button>
          </div>
        </div>
      </div>
    );
  }

  // Before rendering, limit sortedQuestions based on user type
  
  let displayQuestions = sortedQuestions;
  if (isNonPremium) {
    displayQuestions = sortedQuestions.slice(0, 6);
  } else if (isFreeTrial) {
    displayQuestions = sortedQuestions.slice(0, 100);
  }
  // Full premium users and admins: no limit

  // In the navigation display, show the correct total for the current filter
  let navTotal;
  if (showOnlyFavorites || showOnlyUnanswered || orderBy !== 'default') {
    navTotal = displayQuestions.length;
  } else if (selectedCategory || selectedLevel) {
    // For category/level filters, show the filtered count
    navTotal = displayQuestions.length;
  } else {
    navTotal = totalCount; // Show real database count to encourage upgrades
  }

  return (
    <>
      {/* Referral Banner Modal */}
      {showReferralBanner && (
        <ReferralBanner
          onClose={() => {
            setShowReferralBanner(false);
            setReferralBannerDismissed(true);
            localStorage.setItem('referralBannerDismissed', 'true');
          }}
          onNavigate={() => {
            setShowReferralBanner(false);
            navigate('/dashboard');
          }}
          referralStats={referralStats}
          isMobile={isMobile}
        />
      )}
      
      <style>{gradientAnimation}</style>
      <div style={{ 
        direction: 'rtl', 
        padding: isMobile ? '1rem 0.5rem' : '2rem', 
        maxWidth: 800, 
        margin: '0 auto',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '100vh',
        borderRadius: 0
      }}>
        <Card sx={{ 
          borderRadius: 3, 
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)', 
          mb: 3,
          background: 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)'
        }}>
          <CardContent sx={{ p: isMobile ? 2 : 4 }}>
          <h2 style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold', 
            fontSize: isMobile ? 22 : 28, 
            marginBottom: isMobile ? 14 : 22, 
            textAlign: 'right' 
          }}>تمارين</h2>
          
          {/* Free trial notice */}
          {isFreeTrial && (
            <div style={{
              background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
              border: '1px solid rgba(252, 182, 159, 0.3)',
              borderRadius: 16,
              padding: '16px 20px',
              marginBottom: '20px',
              textAlign: 'right',
              fontSize: isMobile ? 14 : 16,
              color: '#8b4513',
              boxShadow: '0 8px 32px rgba(252, 182, 159, 0.2)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(90deg, #ff6b6b, #feca57, #48dbfb, #ff9ff3)',
                animation: 'gradientShift 3s ease infinite'
              }}></div>
              <strong>🔔 تنبيه:</strong> أنت تستخدم النسخة التجريبية المجانية. يمكنك الوصول إلى 100 سؤال فقط. اشترك الآن للوصول إلى جميع الأسئلة والميزات.
            </div>
          )}
          {/* Category and level selectors */}
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'stretch', mb: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                {categories.filter(cat => cat.key !== 'grammar').map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => setSelectedCategory(cat.key)}
                    style={{
                      padding: '0.5rem 1.1rem',
                      borderRadius: 12,
                      border: selectedCategory === cat.key ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                      background: selectedCategory === cat.key 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                      color: selectedCategory === cat.key ? '#fff' : '#333',
                      fontWeight: 'bold',
                      fontSize: 15,
                      cursor: 'pointer',
                      marginBottom: 6,
                      minWidth: 80,
                      boxShadow: selectedCategory === cat.key 
                        ? '0 8px 32px rgba(102, 126, 234, 0.3)'
                        : '0 4px 16px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      transform: selectedCategory === cat.key ? 'translateY(-2px)' : 'translateY(0)'
                    }}
                  >
                    {cat.label}
                  </button>
                ))}
              </Box>
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <div style={{ 
                  marginBottom: 12,
                  fontSize: 16,
                  fontWeight: 'bold',
                  color: '#1e293b',
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}>
                  مستوى الصعوبة:
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: 12,
                  flexWrap: 'wrap'
                }}>
                  {levels.map(lvl => (
                    <button
                      key={lvl.key}
                      onClick={() => setSelectedLevel(lvl.key)}
                      style={{
                        padding: '10px 20px',
                        borderRadius: 12,
                        border: selectedLevel === lvl.key ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                        background: selectedLevel === lvl.key 
                          ? (lvl.key === 'easy' 
                              ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                              : lvl.key === 'middle'
                              ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)')
                          : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                        color: selectedLevel === lvl.key ? '#fff' : '#333',
                        fontWeight: 'bold',
                        fontSize: 16,
                        cursor: 'pointer',
                        minWidth: 80,
                        boxShadow: selectedLevel === lvl.key 
                          ? (lvl.key === 'easy'
                              ? '0 8px 32px rgba(34, 197, 94, 0.3)'
                              : lvl.key === 'middle'
                              ? '0 8px 32px rgba(245, 158, 11, 0.3)'
                              : '0 8px 32px rgba(239, 68, 68, 0.3)')
                          : '0 4px 16px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        transform: selectedLevel === lvl.key ? 'translateY(-2px)' : 'translateY(0)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedLevel !== lvl.key) {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 6px 24px rgba(0,0,0,0.15)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedLevel !== lvl.key) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                        }
                      }}
                    >
                      {selectedLevel === lvl.key && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: lvl.key === 'easy'
                            ? 'linear-gradient(90deg, #22c55e, #16a34a, #15803d, #166534)'
                            : lvl.key === 'middle'
                            ? 'linear-gradient(90deg, #f59e0b, #d97706, #b45309, #92400e)'
                            : 'linear-gradient(90deg, #ef4444, #dc2626, #b91c1c, #991b1b)',
                          animation: 'gradientShift 4s ease infinite'
                        }}></div>
                      )}
                      {lvl.label}
                    </button>
                  ))}
                </div>
              </Box>

              {/* Flashcard Mode Toggle - Only for vocab category (Mobile) */}
              {selectedCategory === 'vocab' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'stretch', mt: 1.5 }}>
                  <div style={{
                    fontWeight: 'bold',
                    fontSize: 15,
                    color: '#1e293b',
                    textAlign: 'center',
                    marginBottom: 8
                  }}>
                    نمط التمرين:
                  </div>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <button
                      onClick={() => {
                        setFlashcardMode(false);
                        setShowFlashcardAnswer(false);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        borderRadius: 12,
                        border: !flashcardMode ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                        background: !flashcardMode
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                        color: !flashcardMode ? '#fff' : '#333',
                        fontWeight: 'bold',
                        fontSize: 14,
                        cursor: 'pointer',
                        boxShadow: !flashcardMode
                          ? '0 8px 32px rgba(102, 126, 234, 0.3)'
                          : '0 4px 16px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        transform: !flashcardMode ? 'translateY(-2px)' : 'translateY(0)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {!flashcardMode && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: 'linear-gradient(90deg, #667eea, #764ba2, #667eea)',
                          animation: 'gradientShift 4s ease infinite'
                        }}></div>
                      )}
                      أسئلة عادية
                    </button>
                    <button
                      onClick={() => {
                        setFlashcardMode(true);
                        setShowFlashcardAnswer(false);
                        setShowFeedback(false);
                        setSelectedAnswer(null);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        borderRadius: 12,
                        border: flashcardMode ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                        background: flashcardMode
                          ? 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)'
                          : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                        color: flashcardMode ? '#fff' : '#333',
                        fontWeight: 'bold',
                        fontSize: 14,
                        cursor: 'pointer',
                        boxShadow: flashcardMode
                          ? '0 8px 32px rgba(6, 182, 212, 0.3)'
                          : '0 4px 16px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        transform: flashcardMode ? 'translateY(-2px)' : 'translateY(0)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {flashcardMode && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: 'linear-gradient(90deg, #06b6d4, #0891b2, #06b6d4)',
                          animation: 'gradientShift 4s ease infinite'
                        }}></div>
                      )}
                      بطاقات تعليمية
                    </button>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-start', mb: 2 }}>
                {categories.filter(cat => cat.key !== 'grammar').map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => setSelectedCategory(cat.key)}
                    style={{
                      padding: '0.7rem 1.7rem',
                      borderRadius: 12,
                      border: selectedCategory === cat.key ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                      background: selectedCategory === cat.key 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                      color: selectedCategory === cat.key ? '#fff' : '#333',
                      fontWeight: 'bold',
                      fontSize: 17,
                      cursor: 'pointer',
                      minWidth: 80,
                      boxShadow: selectedCategory === cat.key 
                        ? '0 8px 32px rgba(102, 126, 234, 0.3)'
                        : '0 4px 16px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      transform: selectedCategory === cat.key ? 'translateY(-2px)' : 'translateY(0)'
                    }}
                  >
                    {cat.label}
                  </button>
                ))}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
                <div style={{ 
                  marginLeft: 12, 
                  fontWeight: 'bold',
                  fontSize: 16,
                  color: '#1e293b',
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  minWidth: 'fit-content'
                }}>
                  مستوى الصعوبة:
                </div>
                <div style={{ 
                  display: 'flex', 
                  gap: 12,
                  flexWrap: 'wrap'
                }}>
                  {levels.map(lvl => (
                    <button
                      key={lvl.key}
                      onClick={() => setSelectedLevel(lvl.key)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 12,
                        border: selectedLevel === lvl.key ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                        background: selectedLevel === lvl.key 
                          ? (lvl.key === 'easy' 
                              ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                              : lvl.key === 'middle'
                              ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)')
                          : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                        color: selectedLevel === lvl.key ? '#fff' : '#333',
                        fontWeight: 'bold',
                        fontSize: 15,
                        cursor: 'pointer',
                        minWidth: 70,
                        boxShadow: selectedLevel === lvl.key 
                          ? (lvl.key === 'easy'
                              ? '0 8px 32px rgba(34, 197, 94, 0.3)'
                              : lvl.key === 'middle'
                              ? '0 8px 32px rgba(245, 158, 11, 0.3)'
                              : '0 8px 32px rgba(239, 68, 68, 0.3)')
                          : '0 4px 16px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        transform: selectedLevel === lvl.key ? 'translateY(-2px)' : 'translateY(0)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedLevel !== lvl.key) {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 6px 24px rgba(0,0,0,0.15)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedLevel !== lvl.key) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                        }
                      }}
                    >
                      {selectedLevel === lvl.key && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: lvl.key === 'easy'
                            ? 'linear-gradient(90deg, #22c55e, #16a34a, #15803d, #166534)'
                            : lvl.key === 'middle'
                            ? 'linear-gradient(90deg, #f59e0b, #d97706, #b45309, #92400e)'
                            : 'linear-gradient(90deg, #ef4444, #dc2626, #b91c1c, #991b1b)',
                          animation: 'gradientShift 4s ease infinite'
                        }}></div>
                      )}
                      {lvl.label}
                    </button>
                  ))}
                </div>
              </Box>

              {/* Flashcard Mode Toggle - Only for vocab category */}
              {selectedCategory === 'vocab' && (
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: 1.5, flexWrap: 'wrap', mt: 2 }}>
                  <div style={{
                    marginLeft: 12,
                    fontWeight: 'bold',
                    fontSize: 16,
                    color: '#1e293b',
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    minWidth: 'fit-content'
                  }}>
                    نمط التمرين:
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap'
                  }}>
                    <button
                      onClick={() => {
                        setFlashcardMode(false);
                        setShowFlashcardAnswer(false);
                      }}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 12,
                        border: !flashcardMode ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                        background: !flashcardMode
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                        color: !flashcardMode ? '#fff' : '#333',
                        fontWeight: 'bold',
                        fontSize: 15,
                        cursor: 'pointer',
                        minWidth: 90,
                        boxShadow: !flashcardMode
                          ? '0 8px 32px rgba(102, 126, 234, 0.3)'
                          : '0 4px 16px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        transform: !flashcardMode ? 'translateY(-2px)' : 'translateY(0)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        if (flashcardMode) {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 6px 24px rgba(0,0,0,0.15)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (flashcardMode) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                        }
                      }}
                    >
                      {!flashcardMode && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: 'linear-gradient(90deg, #667eea, #764ba2, #667eea)',
                          animation: 'gradientShift 4s ease infinite'
                        }}></div>
                      )}
                      أسئلة عادية
                    </button>
                    <button
                      onClick={() => {
                        setFlashcardMode(true);
                        setShowFlashcardAnswer(false);
                        setShowFeedback(false);
                        setSelectedAnswer(null);
                      }}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 12,
                        border: flashcardMode ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                        background: flashcardMode
                          ? 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)'
                          : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                        color: flashcardMode ? '#fff' : '#333',
                        fontWeight: 'bold',
                        fontSize: 15,
                        cursor: 'pointer',
                        minWidth: 90,
                        boxShadow: flashcardMode
                          ? '0 8px 32px rgba(6, 182, 212, 0.3)'
                          : '0 4px 16px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        transform: flashcardMode ? 'translateY(-2px)' : 'translateY(0)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        if (!flashcardMode) {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 6px 24px rgba(0,0,0,0.15)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!flashcardMode) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                        }
                      }}
                    >
                      {flashcardMode && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: 'linear-gradient(90deg, #06b6d4, #0891b2, #06b6d4)',
                          animation: 'gradientShift 4s ease infinite'
                        }}></div>
                      )}
                      بطاقات تعليمية
                    </button>
                  </div>
                </Box>
              )}
            </Box>
          )}
          {/* Filters Toggle Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 16
          }}>
            <button
              onClick={() => setFiltersCollapsed(!filtersCollapsed)}
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '8px 16px',
                fontSize: 14,
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 20px rgba(139, 92, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(139, 92, 246, 0.3)';
              }}
            >
              {filtersCollapsed ? (
                <>
                  <span>👁️</span>
                  إظهار الفلاتر
                </>
              ) : (
                <>
                  <span>🙈</span>
                  إخفاء الفلاتر
                </>
              )}
            </button>
          </div>
          
          {/* Filters and ordering */}
          {!filtersCollapsed && (
            <>
            {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'stretch', mb: 2 }}>
              <button
                onClick={() => setShowOnlyFavorites(fav => !fav)}
                style={{
                  padding: '0.5rem 1.1rem',
                  borderRadius: 12,
                  border: showOnlyFavorites ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                  background: showOnlyFavorites 
                    ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)'
                    : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  color: showOnlyFavorites ? '#fff' : '#333',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginBottom: 6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 15,
                  boxShadow: showOnlyFavorites 
                    ? '0 8px 32px rgba(255, 107, 107, 0.3)'
                    : '0 4px 16px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  transform: showOnlyFavorites ? 'translateY(-2px)' : 'translateY(0)'
                }}
              >
                {showOnlyFavorites ? <FaHeart /> : <FaRegHeart />}
                عرض المفضلة فقط
              </button>
              <button
                onClick={handleShowOnlyUnanswered}
                style={{
                  padding: '0.5rem 1.1rem',
                  borderRadius: 12,
                  border: showOnlyUnanswered ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                  background: showOnlyUnanswered 
                    ? 'linear-gradient(135deg, #48dbfb 0%, #0abde3 100%)'
                    : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  color: showOnlyUnanswered ? '#fff' : '#333',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginBottom: 6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 15,
                  boxShadow: showOnlyUnanswered 
                    ? '0 8px 32px rgba(72, 219, 251, 0.3)'
                    : '0 4px 16px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  transform: showOnlyUnanswered ? 'translateY(-2px)' : 'translateY(0)'
                }}
              >
                <FaQuestionCircle style={{ color: showOnlyUnanswered ? '#fff' : '#bbb' }} />
                عرض الأسئلة غير المجابة
              </button>
              <Box sx={{ mt: 2 }}>
                <div style={{ 
                  marginBottom: 8,
                  fontSize: 15,
                  fontWeight: 'bold',
                  color: '#1e293b',
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}>
                  ترتيب الأسئلة:
                </div>
                <select
                  value={orderBy}
                  onChange={handleOrderChange}
                  style={{ 
                    padding: '8px 16px', 
                    borderRadius: 12, 
                    border: '2px solid rgba(139, 92, 246, 0.3)', 
                    fontWeight: 'bold', 
                    fontSize: 15,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                    color: '#1e293b',
                    cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.1)',
                    transition: 'all 0.3s ease',
                    minWidth: '200px'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '2px solid rgba(139, 92, 246, 0.6)';
                    e.target.style.boxShadow = '0 8px 32px rgba(139, 92, 246, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '2px solid rgba(139, 92, 246, 0.3)';
                    e.target.style.boxShadow = '0 4px 16px rgba(139, 92, 246, 0.1)';
                  }}
                >
                  <option value="default">الافتراضي</option>
                  <option value="wrong">حسب عدد الأخطاء</option>
                  <option value="lastAttempt">حسب أقدم محاولة</option>
                  <option value="random">ترتيب عشوائي</option>
                </select>
              </Box>
            </Box>
          ) : (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'row-reverse', gap: 1.5, alignItems: 'center', mb: 1, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowOnlyFavorites(fav => !fav)}
                  style={{
                    padding: '0.5rem 1.5rem',
                    borderRadius: 12,
                    border: showOnlyFavorites ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                    background: showOnlyFavorites 
                      ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)'
                      : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                    color: showOnlyFavorites ? '#fff' : '#333',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 16,
                    boxShadow: showOnlyFavorites 
                      ? '0 8px 32px rgba(255, 107, 107, 0.3)'
                      : '0 4px 16px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    transform: showOnlyFavorites ? 'translateY(-2px)' : 'translateY(0)'
                  }}
                >
                  {showOnlyFavorites ? <FaHeart /> : <FaRegHeart />}
                  عرض المفضلة فقط
                </button>
                <button
                  onClick={handleShowOnlyUnanswered}
                  style={{
                    padding: '0.5rem 1.5rem',
                    borderRadius: 12,
                    border: showOnlyUnanswered ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                    background: showOnlyUnanswered 
                      ? 'linear-gradient(135deg, #48dbfb 0%, #0abde3 100%)'
                      : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                    color: showOnlyUnanswered ? '#fff' : '#333',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 16,
                    boxShadow: showOnlyUnanswered 
                      ? '0 8px 32px rgba(72, 219, 251, 0.3)'
                      : '0 4px 16px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    transform: showOnlyUnanswered ? 'translateY(-2px)' : 'translateY(0)'
                  }}
                >
                  <FaQuestionCircle style={{ color: showOnlyUnanswered ? '#fff' : '#bbb' }} />
                  عرض الأسئلة غير المجابة
                </button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.5, justifyContent: 'flex-start', mt: 2 }}>
                <div style={{ 
                  fontSize: 16,
                  fontWeight: 'bold',
                  color: '#1e293b',
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  minWidth: 'fit-content'
                }}>
                  ترتيب الأسئلة:
                </div>
                <select
                  value={orderBy}
                  onChange={handleOrderChange}
                  style={{ 
                    padding: '8px 16px', 
                    borderRadius: 12, 
                    border: '2px solid rgba(139, 92, 246, 0.3)', 
                    fontWeight: 'bold', 
                    fontSize: 15,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                    color: '#1e293b',
                    cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.1)',
                    transition: 'all 0.3s ease',
                    minWidth: '200px'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '2px solid rgba(139, 92, 246, 0.6)';
                    e.target.style.boxShadow = '0 8px 32px rgba(139, 92, 246, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '2px solid rgba(139, 92, 246, 0.3)';
                    e.target.style.boxShadow = '0 4px 16px rgba(139, 92, 246, 0.1)';
                  }}
                >
                  <option value="default">الافتراضي</option>
                  <option value="wrong">حسب عدد الأخطاء</option>
                  <option value="lastAttempt">حسب أقدم محاولة</option>
                  <option value="random">ترتيب عشوائي</option>
                </select>
              </Box>
            </Box>
          )}
          </>
          )}
          <Divider sx={{ my: isMobile ? 1.5 : 3 }} />
          {/* FULL FEATURED QUESTION RENDERING SECTION START */}
          {loading ? (
            <div style={{ textAlign: 'center', color: '#1976d2', fontWeight: 'bold', fontSize: 20, margin: '2rem 0' }}>جاري التحميل...</div>
          ) : error ? (
            <div style={{ textAlign: 'center', color: '#e11d48', fontWeight: 'bold', fontSize: 20, margin: '2rem 0' }}>{error}</div>
          ) : selectedCategory === 'grammar' ? (
            <div style={{ textAlign: 'center', margin: '3rem 0' }}>
              <div style={{ fontSize: isMobile ? 48 : 64, marginBottom: '1rem' }}>📚</div>
              <h2 style={{ color: '#1976d2', fontWeight: 'bold', fontSize: isMobile ? 24 : 32, marginBottom: '1rem' }}>
                قسم القواعد
              </h2>
              <div style={{ 
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
                borderRadius: 12, 
                padding: isMobile ? '1.5rem' : '2rem', 
                margin: '0 auto', 
                maxWidth: 500,
                border: '2px solid #f59e0b'
              }}>
                <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 'bold', color: '#92400e', marginBottom: '0.5rem' }}>
                  قريباً
                </div>
                <div style={{ fontSize: isMobile ? 16 : 18, color: '#92400e', lineHeight: 1.5 }}>
                  سنقوم بإضافة أسئلة القواعد قريباً. 
                  <br />
                  ابقوا متابعين للتحديثات!
                </div>
              </div>
            </div>
          ) : selectedCategory === 'reading' ? (
            filteredReadingArticles.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#888', fontWeight: 'bold', fontSize: 20, margin: '2rem 0' }}>لا يوجد مقالات متاحة</div>
            ) : (
              <div>
                {/* Article title and text */}
                {user && selectedCategory === 'reading' && (
                  <div style={{ marginBottom: 16, textAlign: 'right' }}>
                    <button
                      onClick={toggleArticleFavorite}
                      style={{ 
                        background: isArticleFavorite 
                          ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                          : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                        border: isArticleFavorite ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                        color: isArticleFavorite ? '#fff' : '#666',
                        borderRadius: 16,
                        padding: '10px 18px',
                        fontSize: 15,
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        boxShadow: isArticleFavorite 
                          ? '0 8px 32px rgba(239, 68, 68, 0.4)'
                          : '0 4px 16px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        transform: isArticleFavorite ? 'translateY(-2px)' : 'translateY(0)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        if (isArticleFavorite) {
                          e.target.style.transform = 'translateY(-3px)';
                          e.target.style.boxShadow = '0 12px 40px rgba(239, 68, 68, 0.5)';
                        } else {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 6px 24px rgba(0,0,0,0.15)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (isArticleFavorite) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 8px 32px rgba(239, 68, 68, 0.4)';
                        } else {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                        }
                      }}
                    >
                      {isArticleFavorite && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: 'linear-gradient(90deg, #ef4444, #dc2626, #b91c1c, #991b1b)',
                          animation: 'gradientShift 4s ease infinite'
                        }}></div>
                      )}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        {isArticleFavorite ? (
                          <FaHeart style={{ 
                            fontSize: 18,
                            filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))'
                          }} />
                        ) : (
                          <FaRegHeart style={{ 
                            fontSize: 18,
                            color: '#666'
                          }} />
                        )}
                        <span style={{ 
                          fontSize: 14,
                          fontWeight: 'bold'
                        }}>
                          {isArticleFavorite ? 'إزالة من المفضلة' : 'إضافة للمفضلة'}
                        </span>
                      </div>
                    </button>
                  </div>
                )}
                <div style={{ fontWeight: 'bold', fontSize: isMobile ? 18 : 22, marginBottom: 12 }}>{filteredReadingArticles[currentReadingArticleIndex].title}</div>
                <div style={{ background: '#f3f4f6', borderRadius: 8, padding: isMobile ? 10 : 18, marginBottom: 18, fontSize: isMobile ? 15 : 17 }}>
                  {(filteredReadingArticles[currentReadingArticleIndex].article || filteredReadingArticles[currentReadingArticleIndex].text || '').split('\n\n').map((paragraph, idx) => (
                    <p key={idx} style={{ margin: idx > 0 ? '12px 0 0 0' : '0 0 12px 0', lineHeight: '1.6' }}>
                      {paragraph}
                    </p>
                  ))}
                </div>
                {/* Article Vocabulary */}
                {readingArticleAnswered[currentReadingArticleIndex] &&
                  filteredReadingArticles[currentReadingArticleIndex].articleVocab &&
                  filteredReadingArticles[currentReadingArticleIndex].articleVocab.length > 0 && (
                    <div style={{ marginTop: 16, background: '#f3f4f6', borderRadius: 8, padding: '10px 14px' }}>
                      {/* Header with toggle button */}
                      <div style={{
                        fontWeight: 'bold',
                        fontSize: 15,
                        marginBottom: 8,
                        color: '#1976d2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <span>الكلمات المهمة في المقالة:</span>

                        {/* Mode Toggle */}
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <button
                            onClick={() => {
                              setArticleVocabDisplayMode('list');
                              setCurrentArticleVocabCardIndex(0);
                              setShowArticleVocabCardAnswer(false);
                            }}
                            style={{
                              padding: '5px 10px',
                              borderRadius: 6,
                              border: articleVocabDisplayMode === 'list' ? '2px solid #1976d2' : '1px solid rgba(25, 118, 210, 0.3)',
                              background: articleVocabDisplayMode === 'list'
                                ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
                                : 'rgba(255, 255, 255, 0.9)',
                              color: articleVocabDisplayMode === 'list' ? '#fff' : '#1976d2',
                              fontWeight: 'bold',
                              fontSize: 12,
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            📋 قائمة
                          </button>
                          <button
                            onClick={() => {
                              setArticleVocabDisplayMode('cards');
                              setCurrentArticleVocabCardIndex(0);
                              setShowArticleVocabCardAnswer(false);
                            }}
                            style={{
                              padding: '5px 10px',
                              borderRadius: 6,
                              border: articleVocabDisplayMode === 'cards' ? '2px solid #06b6d4' : '1px solid rgba(6, 182, 212, 0.3)',
                              background: articleVocabDisplayMode === 'cards'
                                ? 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)'
                                : 'rgba(255, 255, 255, 0.9)',
                              color: articleVocabDisplayMode === 'cards' ? '#fff' : '#06b6d4',
                              fontWeight: 'bold',
                              fontSize: 12,
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            🎴 بطاقات
                          </button>
                        </div>
                      </div>

                      {/* List Mode */}
                      {articleVocabDisplayMode === 'list' ? (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {filteredReadingArticles[currentReadingArticleIndex].articleVocab.map((v, i) => (
                            <li key={i} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 6 }}>
                              <span style={{ background: '#fff', borderRadius: 6, padding: '4px 10px', marginLeft: 8, fontWeight: 'bold', color: '#1e293b', fontSize: 15 }}>{v.word}</span>
                              <span style={{ color: '#64748b', fontSize: 15 }}>{v.meaning}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        /* Cards Mode */
                        <div>
                          {!showArticleVocabCardAnswer ? (
                            /* Front of card */
                            <div
                              onClick={() => setShowArticleVocabCardAnswer(true)}
                              style={{
                                minHeight: isMobile ? 160 : 200,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#fff',
                                borderRadius: 10,
                                padding: '2rem 1.5rem',
                                cursor: 'pointer',
                                border: '2px solid rgba(25, 118, 210, 0.2)',
                                transition: 'all 0.3s ease',
                                marginBottom: 10
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(25, 118, 210, 0.2)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <div style={{
                                fontSize: isMobile ? 11 : 13,
                                color: '#1976d2',
                                marginBottom: isMobile ? 14 : 18,
                                fontWeight: 'bold'
                              }}>
                                كلمة {currentArticleVocabCardIndex + 1} من {filteredReadingArticles[currentReadingArticleIndex].articleVocab.length}
                              </div>

                              <div style={{
                                fontSize: isMobile ? 26 : 32,
                                fontWeight: 'bold',
                                color: '#1e293b',
                                marginBottom: isMobile ? 18 : 24,
                                textAlign: 'center'
                              }}>
                                {filteredReadingArticles[currentReadingArticleIndex].articleVocab[currentArticleVocabCardIndex].word}
                              </div>

                              <div style={{
                                fontSize: isMobile ? 12 : 14,
                                color: '#1976d2',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                background: 'rgba(25, 118, 210, 0.1)',
                                padding: '8px 16px',
                                borderRadius: 6
                              }}>
                                <span style={{ fontSize: 14 }}>👇</span>
                                اضغط للترجمة
                              </div>
                            </div>
                          ) : (
                            /* Back of card */
                            <div
                              style={{
                                minHeight: isMobile ? 160 : 200,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#fff',
                                borderRadius: 10,
                                padding: '2rem 1.5rem',
                                border: '2px solid rgba(25, 118, 210, 0.2)',
                                marginBottom: 10
                              }}
                            >
                              <div style={{
                                fontSize: isMobile ? 11 : 13,
                                color: '#1976d2',
                                marginBottom: isMobile ? 10 : 14,
                                fontWeight: 'bold'
                              }}>
                                كلمة {currentArticleVocabCardIndex + 1} من {filteredReadingArticles[currentReadingArticleIndex].articleVocab.length}
                              </div>

                              <div style={{
                                fontSize: isMobile ? 22 : 28,
                                fontWeight: 'bold',
                                color: '#1e293b',
                                marginBottom: 8
                              }}>
                                {filteredReadingArticles[currentReadingArticleIndex].articleVocab[currentArticleVocabCardIndex].word}
                              </div>

                              <div style={{
                                fontSize: isMobile ? 16 : 22,
                                color: '#059669',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}>
                                <span style={{ fontSize: isMobile ? 18 : 22 }}>↓</span>
                                {filteredReadingArticles[currentReadingArticleIndex].articleVocab[currentArticleVocabCardIndex].meaning}
                              </div>
                            </div>
                          )}

                          {/* Navigation buttons */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                            <button
                              onClick={() => {
                                if (currentArticleVocabCardIndex > 0) {
                                  setCurrentArticleVocabCardIndex(currentArticleVocabCardIndex - 1);
                                  setShowArticleVocabCardAnswer(false);
                                }
                              }}
                              disabled={currentArticleVocabCardIndex === 0}
                              style={{
                                flex: 1,
                                padding: '8px 16px',
                                borderRadius: 8,
                                border: 'none',
                                background: currentArticleVocabCardIndex === 0
                                  ? 'rgba(0,0,0,0.1)'
                                  : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                color: currentArticleVocabCardIndex === 0 ? '#999' : '#fff',
                                fontWeight: 'bold',
                                fontSize: 14,
                                cursor: currentArticleVocabCardIndex === 0 ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              → السابق
                            </button>
                            <button
                              onClick={() => {
                                if (currentArticleVocabCardIndex < filteredReadingArticles[currentReadingArticleIndex].articleVocab.length - 1) {
                                  setCurrentArticleVocabCardIndex(currentArticleVocabCardIndex + 1);
                                  setShowArticleVocabCardAnswer(false);
                                }
                              }}
                              disabled={currentArticleVocabCardIndex === filteredReadingArticles[currentReadingArticleIndex].articleVocab.length - 1}
                              style={{
                                flex: 1,
                                padding: '8px 16px',
                                borderRadius: 8,
                                border: 'none',
                                background: currentArticleVocabCardIndex === filteredReadingArticles[currentReadingArticleIndex].articleVocab.length - 1
                                  ? 'rgba(0,0,0,0.1)'
                                  : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                color: currentArticleVocabCardIndex === filteredReadingArticles[currentReadingArticleIndex].articleVocab.length - 1 ? '#999' : '#fff',
                                fontWeight: 'bold',
                                fontSize: 14,
                                cursor: currentArticleVocabCardIndex === filteredReadingArticles[currentReadingArticleIndex].articleVocab.length - 1 ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              ← التالي
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                )}
                {filteredReadingArticles[currentReadingArticleIndex].questions.map((q, idx) => (
                  <div key={idx} style={{ marginBottom: 24, border: '1px solid #e0e7ef', borderRadius: 8, padding: isMobile ? 10 : 18, background: '#fff' }}>
                    <div style={{ fontWeight: 'bold', fontSize: isMobile ? 16 : 18, marginBottom: 8 }}>{q.question || q.sentence || q.text || ''}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {shuffledReadingChoices[idx] && shuffledReadingChoices[idx].map((choice, cidx) => (
                        <button
                          key={cidx}
                          onClick={() => handleReadingAnswer(choice, idx)}
                          disabled={readingFeedback[idx]}
                          style={{
                            background: readingAnswers[idx] === choice 
                              ? (choice === q.correct 
                                ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)')
                              : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                            color: readingAnswers[idx] === choice ? '#fff' : '#333',
                            border: readingAnswers[idx] === choice 
                              ? '2px solid transparent'
                              : '1px solid rgba(0,0,0,0.1)',
                            borderRadius: 12,
                            padding: isMobile ? '0.8rem 1.2rem' : '1rem 1.8rem',
                            fontWeight: 'bold',
                            fontSize: isMobile ? 15 : 16,
                            cursor: readingFeedback[idx] ? 'not-allowed' : 'pointer',
                            boxShadow: readingAnswers[idx] === choice 
                              ? (choice === q.correct 
                                ? '0 8px 32px rgba(34, 197, 94, 0.3)'
                                : '0 8px 32px rgba(239, 68, 68, 0.3)')
                              : '0 4px 16px rgba(0,0,0,0.1)',
                            transition: 'all 0.3s ease',
                            transform: readingAnswers[idx] === choice ? 'translateY(-2px)' : 'translateY(0)',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                          onMouseEnter={(e) => {
                            if (!readingFeedback[idx] && readingAnswers[idx] !== choice) {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!readingFeedback[idx] && readingAnswers[idx] !== choice) {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                            }
                          }}
                        >
                          {readingAnswers[idx] === choice && (
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '2px',
                              background: choice === q.correct 
                                ? 'linear-gradient(90deg, #22c55e, #16a34a, #15803d)'
                                : 'linear-gradient(90deg, #ef4444, #dc2626, #b91c1c)',
                              animation: 'gradientShift 2s ease infinite'
                            }}></div>
                          )}
                          {choice}
                        </button>
                      ))}
                    </div>
                    {/* Hint for reading sub-question */}
                    {q.hint && (
                      <button 
                        onClick={() => setReadingShowHint(hints => {
                          const arr = [...hints];
                          arr[idx] = !arr[idx];
                          return arr;
                        })} 
                        style={{ 
                          marginTop: 12, 
                          background: readingShowHint[idx] 
                            ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                            : 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                          border: '2px solid transparent',
                          color: readingShowHint[idx] ? '#92400e' : '#fff',
                          fontWeight: 'bold', 
                          borderRadius: 12, 
                          padding: '10px 16px', 
                          fontSize: 15, 
                          cursor: 'pointer',
                          boxShadow: readingShowHint[idx] 
                            ? '0 8px 32px rgba(245, 158, 11, 0.3)'
                            : '0 8px 32px rgba(251, 191, 36, 0.4)',
                          transition: 'all 0.3s ease',
                          transform: readingShowHint[idx] ? 'translateY(-2px)' : 'translateY(0)',
                          position: 'relative',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6
                        }}
                        onMouseEnter={(e) => {
                          if (!readingShowHint[idx]) {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 12px 40px rgba(251, 191, 36, 0.5)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!readingShowHint[idx]) {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 8px 32px rgba(251, 191, 36, 0.4)';
                          }
                        }}
                      >
                        {readingShowHint[idx] && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '2px',
                            background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)',
                            animation: 'gradientShift 2s ease infinite'
                          }}></div>
                        )}
                        <FaRegLightbulb style={{ 
                          marginLeft: 6,
                          fontSize: readingShowHint[idx] ? 16 : 18,
                          filter: readingShowHint[idx] ? 'none' : 'brightness(1.2)'
                        }} /> 
                        {readingShowHint[idx] ? 'إخفاء التلميح' : 'عرض تلميح'}
                      </button>
                    )}
                    {q.hint && readingShowHint[idx] && (
                      <div style={{ 
                        marginTop: 12, 
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
                        color: '#92400e', 
                        borderRadius: 12, 
                        padding: '14px 18px', 
                        fontSize: 15,
                        border: '1px solid rgba(245, 158, 11, 0.2)',
                        boxShadow: '0 8px 32px rgba(245, 158, 11, 0.15)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '2px',
                          background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)',
                          animation: 'gradientShift 3s ease infinite'
                        }}></div>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontSize: 16, marginLeft: 6 }}>💡</span>
                          <span style={{ fontWeight: 'bold' }}>التلميح:</span>
                        </div>
                        {q.hint}
                      </div>
                    )}
                    {readingFeedback[idx] && (
                      <div 
                        ref={feedbackRef} 
                        style={{ 
                          marginTop: 16, 
                          padding: '12px 16px',
                          borderRadius: 12,
                          background: readingAnswers[idx] === q.correct 
                            ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
                            : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                          border: readingAnswers[idx] === q.correct 
                            ? '1px solid rgba(34, 197, 94, 0.2)'
                            : '1px solid rgba(239, 68, 68, 0.2)',
                          color: readingAnswers[idx] === q.correct ? '#166534' : '#991b1b',
                          fontWeight: 'bold', 
                          fontSize: isMobile ? 15 : 16,
                          boxShadow: readingAnswers[idx] === q.correct 
                            ? '0 8px 32px rgba(34, 197, 94, 0.15)'
                            : '0 8px 32px rgba(239, 68, 68, 0.15)',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {readingAnswers[idx] === q.correct && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '2px',
                            background: 'linear-gradient(90deg, #22c55e, #16a34a, #15803d)',
                            animation: 'gradientShift 2s ease infinite'
                          }}></div>
                        )}
                        {readingAnswers[idx] === q.correct ? '✅ إجابة صحيحة!' : `❌ إجابة خاطئة. الإجابة الصحيحة: ${q.correct}`}
                      </div>
                    )}
                    {/* Explanation for reading sub-question */}
                    {readingFeedback[idx] && q.explanation && (
                      <div style={{ 
                        marginTop: 16, 
                        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', 
                        color: '#1e293b', 
                        borderRadius: 12, 
                        padding: '16px 20px', 
                        fontSize: 15, 
                        border: '1px solid rgba(14, 165, 233, 0.2)',
                        boxShadow: '0 8px 32px rgba(14, 165, 233, 0.15)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '2px',
                          background: 'linear-gradient(90deg, #0ea5e9, #0284c7, #0369a1)',
                          animation: 'gradientShift 3s ease infinite'
                        }}></div>
                        <div style={{ 
                          fontWeight: 'bold', 
                          marginBottom: 8, 
                          color: '#0369a1',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <span style={{ fontSize: 18, marginLeft: 8 }}>💡</span>
                          شرح الإجابة:
                        </div>
                        {q.explanation}
                      </div>
                    )}
                    {/* Enhanced Stats for sub-question */}
                    {user && (
                      <div style={{ 
                        marginTop: 12, 
                        padding: '12px 16px',
                        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                        borderRadius: 10,
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        boxShadow: '0 4px 16px rgba(148, 163, 184, 0.1)'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 12,
                          flexWrap: 'wrap'
                        }}>
                          {/* Correct Answers */}
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 6,
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                            borderRadius: 8,
                            border: '1px solid rgba(34, 197, 94, 0.2)'
                          }}>
                            <span style={{ 
                              fontSize: 16, 
                              fontWeight: 'bold', 
                              color: '#15803d' 
                            }}>
                              {getStatsForArticleQuestion(filteredReadingArticles[currentReadingArticleIndex]._id && filteredReadingArticles[currentReadingArticleIndex]._id.toString(), idx).correct || 0}
                            </span>
                            <span style={{ 
                              fontSize: 12, 
                              color: '#166534' 
                            }}>
                              صحيح
                            </span>
                          </div>
                          
                          {/* Wrong Answers */}
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 6,
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                            borderRadius: 8,
                            border: '1px solid rgba(239, 68, 68, 0.2)'
                          }}>
                            <span style={{ 
                              fontSize: 16, 
                              fontWeight: 'bold', 
                              color: '#b91c1c' 
                            }}>
                              {getStatsForArticleQuestion(filteredReadingArticles[currentReadingArticleIndex]._id && filteredReadingArticles[currentReadingArticleIndex]._id.toString(), idx).wrong || 0}
                            </span>
                            <span style={{ 
                              fontSize: 12, 
                              color: '#991b1b' 
                            }}>
                              خطأ
                            </span>
                          </div>
                          
                          {/* Last Attempt */}
                          {getStatsForArticleQuestion(filteredReadingArticles[currentReadingArticleIndex]._id && filteredReadingArticles[currentReadingArticleIndex]._id.toString(), idx).lastAttempt && (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 6,
                              padding: '6px 12px',
                              background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                              borderRadius: 8,
                              border: '1px solid rgba(59, 130, 246, 0.2)'
                            }}>
                              <span style={{ 
                                fontSize: 12, 
                                color: '#1e40af' 
                              }}>
                                🕒 {timeAgo(getStatsForArticleQuestion(filteredReadingArticles[currentReadingArticleIndex]._id && filteredReadingArticles[currentReadingArticleIndex]._id.toString(), idx).lastAttempt)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {/* Article navigation */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
                  <button 
                    onClick={handlePrevArticle} 
                    disabled={currentReadingArticleIndex === 0} 
                    style={{ 
                      padding: '10px 20px', 
                      borderRadius: 12, 
                      border: currentReadingArticleIndex === 0 ? '1px solid rgba(0,0,0,0.1)' : '2px solid transparent',
                      background: currentReadingArticleIndex === 0 
                        ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.3) 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: currentReadingArticleIndex === 0 ? '#999' : '#fff',
                      fontWeight: 'bold', 
                      fontSize: 16, 
                      cursor: currentReadingArticleIndex === 0 ? 'not-allowed' : 'pointer',
                      boxShadow: currentReadingArticleIndex === 0 
                        ? '0 4px 16px rgba(0,0,0,0.1)'
                        : '0 8px 32px rgba(102, 126, 234, 0.3)',
                      transition: 'all 0.3s ease',
                      transform: currentReadingArticleIndex === 0 ? 'translateY(0)' : 'translateY(0)'
                    }}
                    onMouseEnter={(e) => {
                      if (currentReadingArticleIndex !== 0) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentReadingArticleIndex !== 0) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.3)';
                      }
                    }}
                  >
                    السابق
                  </button>
                  <span style={{ 
                    fontWeight: 'bold', 
                    fontSize: 16,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    padding: '10px 20px',
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                  }}>
                    المقالة {currentReadingArticleIndex + 1} من {isFreeTrial ? Math.min(readingArticlesTotalCount, 100) : readingArticlesTotalCount}
                  </span>
                  <button 
                    onClick={handleNextArticle} 
                    disabled={currentReadingArticleIndex === filteredReadingArticles.length - 1} 
                    style={{ 
                      padding: '10px 20px', 
                      borderRadius: 12, 
                      border: currentReadingArticleIndex === filteredReadingArticles.length - 1 ? '1px solid rgba(0,0,0,0.1)' : '2px solid transparent',
                      background: currentReadingArticleIndex === filteredReadingArticles.length - 1 
                        ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.3) 100%)'
                        : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      color: currentReadingArticleIndex === filteredReadingArticles.length - 1 ? '#999' : '#fff',
                      fontWeight: 'bold', 
                      fontSize: 16, 
                      cursor: currentReadingArticleIndex === filteredReadingArticles.length - 1 ? 'not-allowed' : 'pointer',
                      boxShadow: currentReadingArticleIndex === filteredReadingArticles.length - 1 
                        ? '0 4px 16px rgba(0,0,0,0.1)'
                        : '0 8px 32px rgba(34, 197, 94, 0.3)',
                      transition: 'all 0.3s ease',
                      transform: currentReadingArticleIndex === filteredReadingArticles.length - 1 ? 'translateY(0)' : 'translateY(0)'
                    }}
                    onMouseEnter={(e) => {
                      if (currentReadingArticleIndex !== filteredReadingArticles.length - 1) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 12px 40px rgba(34, 197, 94, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentReadingArticleIndex !== filteredReadingArticles.length - 1) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 8px 32px rgba(34, 197, 94, 0.3)';
                      }
                    }}
                  >
                    التالي
                  </button>
                </div>
              </div>
            )
          ) : selectedCategory === 'essay' ? (
            sortedQuestions.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#888', fontWeight: 'bold', fontSize: 20, margin: '2rem 0' }}>لا يوجد أسئلة مقال متاحة</div>
            ) : (
              <div>
                {/* Essay question */}
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ color: '#1976d2', fontSize: isMobile ? 18 : 22, marginBottom: 16, textAlign: 'right' }}>
                    {sortedQuestions[currentIndex]?.topic || sortedQuestions[currentIndex]?.question || 'اكتب مقالاً حول الموضوع التالي'}
                  </h3>
                  
                  {/* Essay requirements */}
                  <div style={{ 
                    background: '#f0f9ff', 
                    borderRadius: 8, 
                    padding: '12px 16px', 
                    marginBottom: 16,
                    border: '1px solid #bae6fd'
                  }}>
                    <ul style={{ margin: 0, paddingRight: 20, color: '#0369a1', fontSize: 15 }}>
                      <li>עליכם לכתוב על הנושא בהיקף של 120 עד 150 מילים.</li>
                      <li>שימו לב שהחיבור יתאים לנושא, והשתדלו לכתוב באופן מסודר ובלשון נכונה.</li>
                    </ul>
                  </div>

                  {/* Essay textarea */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 8, fontSize: 16, textAlign: 'right' }}>
                      כתוב את החיבור שלך כאן:
                    </label>
                    <textarea
                      value={essayAnswers[currentIndex] || ''}
                      onChange={(e) => {
                        const newAnswers = [...essayAnswers];
                        newAnswers[currentIndex] = e.target.value;
                        setEssayAnswers(newAnswers);
                      }}
                      rows={12}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: 8,
                        border: '1px solid #ccc',
                        fontSize: 16,
                        lineHeight: 1.6,
                        resize: 'vertical',
                        direction: 'rtl',
                        fontFamily: 'inherit'
                      }}
                      placeholder="התחל בכתיבת החיבור שלך כאן..."
                    />
                  </div>

                  {/* Word count */}
                  <div style={{ 
                    textAlign: 'right', 
                    fontSize: 14, 
                    color: '#666', 
                    marginBottom: 16 
                  }}>
                    מספר המילים: {(essayAnswers[currentIndex] || '').split(/\s+/).filter(word => word.length > 0).length}
                  </div>

                  {/* Usage indicator for premium users */}
                  {user && user.premium && user.essayReviewUsage && (
                    <div style={{ 
                      textAlign: 'right', 
                      fontSize: 14, 
                      color: '#059669', 
                      marginBottom: 16,
                      background: '#f0fdf4',
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: '1px solid #bbf7d0'
                    }}>
                      المراجعات المتبقية اليوم: {10 - (user.essayReviewUsage.count || 0)} من 10
                    </div>
                  )}

                  {/* Free trial restriction message */}
                  {user && user.hasReceivedFreeTrial && (
                    <div style={{ 
                      background: '#fef2f2', 
                      borderRadius: 8, 
                      padding: '12px 16px',
                      border: '1px solid #fecaca',
                      marginBottom: 16,
                      textAlign: 'right'
                    }}>
                      <div style={{ color: '#dc2626', fontWeight: 'bold', fontSize: 16, marginBottom: 4 }}>
                        ⚠️ ميزة مراجعة المقالات غير متاحة
                      </div>
                      <div style={{ color: '#991b1b', fontSize: 14 }}>
                        ميزة مراجعة المقالات غير متاحة للمستخدمين في الفترة التجريبية المجانية. اشترك الآن للاستفادة من هذه الميزة.
                      </div>
                      <a 
                        href="/register" 
                        style={{ 
                          display: 'inline-block',
                          marginTop: 8,
                          background: '#dc2626', 
                          color: '#fff', 
                          borderRadius: 6, 
                          padding: '8px 16px', 
                          fontWeight: 'bold', 
                          textDecoration: 'none',
                          fontSize: 14
                        }}
                      >
                        اشترك الآن
                      </a>
                    </div>
                  )}

                  {/* Submit button */}
                  <button
                    onClick={handleEssayReview}
                    disabled={essaySubmitted[currentIndex] || essayReviewLoading || !essayAnswers[currentIndex] || (user && user.hasReceivedFreeTrial)}
                    style={{
                      background: essaySubmitted[currentIndex] ? '#22c55e' : essayReviewLoading ? '#9ca3af' : (user && user.hasReceivedFreeTrial) ? '#9ca3af' : '#1976d2',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '12px 24px',
                      fontWeight: 'bold',
                      fontSize: 16,
                      cursor: essaySubmitted[currentIndex] || essayReviewLoading || (user && user.hasReceivedFreeTrial) ? 'default' : 'pointer',
                      marginBottom: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    {essayReviewLoading ? (
                      <>
                        <div style={{ width: '16px', height: '16px', border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                        جاري مراجعة المقال...
                      </>
                    ) : essaySubmitted[currentIndex] ? (
                      'تم إرسال المقال ✓'
                    ) : (user && user.hasReceivedFreeTrial) ? (
                      'غير متاح في الفترة التجريبية'
                    ) : (
                      'إرسال المقال للمراجعة'
                    )}
                  </button>

                  {/* AI Feedback after submission */}
                  {essaySubmitted[currentIndex] && essayFeedback[currentIndex] && (
                    <div style={{ 
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
                      borderRadius: 16, 
                      padding: '20px 24px',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      marginBottom: 20,
                      boxShadow: '0 12px 40px rgba(245, 158, 11, 0.2)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309, #92400e)',
                        animation: 'gradientShift 4s ease infinite'
                      }}></div>
                      <div style={{ 
                        color: '#92400e', 
                        fontWeight: 'bold', 
                        fontSize: 20, 
                        marginBottom: 16, 
                        textAlign: 'right',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end'
                      }}>
                        <span style={{ fontSize: 24, marginLeft: 12 }}>🤖</span>
                        مراجعة المقال بواسطة الذكاء الاصطناعي
                      </div>
                      <div style={{ 
                        color: '#78350f', 
                        fontSize: 16, 
                        lineHeight: 1.7,
                        textAlign: 'right',
                        whiteSpace: 'pre-wrap',
                        direction: 'rtl',
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.4) 100%)',
                        padding: '16px 20px',
                        borderRadius: 12,
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}>
                        {essayFeedback[currentIndex]}
                      </div>
                    </div>
                  )}

                  {/* Success message after submission */}
                  {essaySubmitted[currentIndex] && (
                    <div style={{ 
                      background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', 
                      borderRadius: 16, 
                      padding: '16px 20px',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      boxShadow: '0 8px 32px rgba(34, 197, 94, 0.2)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #22c55e, #16a34a, #15803d, #166534)',
                        animation: 'gradientShift 3s ease infinite'
                      }}></div>
                      <div style={{ 
                        color: '#166534', 
                        fontWeight: 'bold', 
                        fontSize: 18, 
                        marginBottom: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end'
                      }}>
                        <span style={{ fontSize: 20, marginLeft: 8 }}>🎉</span>
                        تم إرسال مقالك بنجاح! ✓
                      </div>
                      <div style={{ 
                        color: '#15803d', 
                        fontSize: 15,
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.4) 100%)',
                        padding: '12px 16px',
                        borderRadius: 8,
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}>
                        يمكنك الانتقال للسؤال التالي أو مراجعة مقالك.
                      </div>
                    </div>
                  )}

                  {/* Navigation for essay */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
                    <button 
                      onClick={handlePrev} 
                      disabled={currentIndex === 0} 
                      style={{ 
                        padding: '10px 20px', 
                        borderRadius: 12, 
                        border: currentIndex === 0 ? '1px solid rgba(0,0,0,0.1)' : '2px solid transparent',
                        background: currentIndex === 0 
                          ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.3) 100%)'
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: currentIndex === 0 ? '#999' : '#fff',
                        fontWeight: 'bold', 
                        fontSize: 16, 
                        cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                        boxShadow: currentIndex === 0 
                          ? '0 4px 16px rgba(0,0,0,0.1)'
                          : '0 8px 32px rgba(102, 126, 234, 0.3)',
                        transition: 'all 0.3s ease',
                        transform: currentIndex === 0 ? 'translateY(0)' : 'translateY(0)'
                      }}
                      onMouseEnter={(e) => {
                        if (currentIndex !== 0) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentIndex !== 0) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.3)';
                        }
                      }}
                    >
                      السابق
                    </button>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                      padding: '10px 20px',
                      borderRadius: 12,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                    }}>
                      <span style={{ 
                        fontWeight: 'bold', 
                        fontSize: 16
                      }}>
                        السؤال {currentIndex + 1} من {navTotal}
                      </span>
                      
                      {/* Quick Navigation - only show when no filters/ordering */}
                      {shouldShowNavigation && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {!showJumpInput ? (
                            <button
                              onClick={() => setShowJumpInput(true)}
                              style={{
                                background: 'rgba(102, 126, 234, 0.1)',
                                border: '1px solid rgba(102, 126, 234, 0.3)',
                                borderRadius: 6,
                                padding: '4px 8px',
                                fontSize: 12,
                                color: '#667eea',
                                cursor: 'pointer',
                                fontWeight: 'bold'
                              }}
                              title="انتقل لرقم سؤال محدد"
                            >
                              🔢
                            </button>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <input
                                type="number"
                                value={jumpToQuestion}
                                onChange={(e) => setJumpToQuestion(e.target.value)}
                                onKeyDown={handleJumpKeyPress}
                                placeholder="رقم"
                                min="1"
                                max={sortedQuestions.length}
                                style={{
                                  width: 50,
                                  padding: '4px 6px',
                                  border: '1px solid #ddd',
                                  borderRadius: 4,
                                  fontSize: 12,
                                  textAlign: 'center'
                                }}
                                autoFocus
                              />
                              <button
                                onClick={handleJumpToQuestion}
                                style={{
                                  background: '#667eea',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 4,
                                  padding: '4px 8px',
                                  fontSize: 11,
                                  cursor: 'pointer'
                                }}
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => {
                                  setShowJumpInput(false);
                                  setJumpToQuestion('');
                                }}
                                style={{
                                  background: '#dc2626',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 4,
                                  padding: '4px 8px',
                                  fontSize: 11,
                                  cursor: 'pointer'
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleNext}
                      disabled={currentIndex === sortedQuestions.length - 1}
                      style={{ 
                        padding: '10px 20px', 
                        borderRadius: 12, 
                        border: currentIndex === sortedQuestions.length - 1 ? '1px solid rgba(0,0,0,0.1)' : '2px solid transparent',
                        background: currentIndex === sortedQuestions.length - 1 
                          ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.3) 100%)'
                          : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        color: currentIndex === sortedQuestions.length - 1 ? '#999' : '#fff',
                        fontWeight: 'bold', 
                        fontSize: 16, 
                        cursor: currentIndex === sortedQuestions.length - 1 ? 'not-allowed' : 'pointer',
                        boxShadow: currentIndex === sortedQuestions.length - 1 
                          ? '0 4px 16px rgba(0,0,0,0.1)'
                          : '0 8px 32px rgba(34, 197, 94, 0.3)',
                        transition: 'all 0.3s ease',
                        transform: currentIndex === sortedQuestions.length - 1 ? 'translateY(0)' : 'translateY(0)'
                      }}
                      onMouseEnter={(e) => {
                        if (currentIndex !== sortedQuestions.length - 1) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 12px 40px rgba(34, 197, 94, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentIndex !== sortedQuestions.length - 1) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 8px 32px rgba(34, 197, 94, 0.3)';
                        }
                      }}
                    >
                      التالي
                    </button>
                  </div>
                </div>
              </div>
            )
          ) : sortedQuestions.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#888', fontWeight: 'bold', fontSize: 20, margin: '2rem 0' }}>لا يوجد أسئلة متاحة</div>
          ) : flashcardMode && selectedCategory === 'vocab' ? (
            <div>
              {/* Flashcard Mode */}
              {!showFlashcardAnswer ? (
                // Front of flashcard - show only the Hebrew word
                <div
                  onClick={() => setShowFlashcardAnswer(true)}
                  style={{
                    minHeight: isMobile ? 250 : 350,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                    borderRadius: 20,
                    padding: '3rem 2rem',
                    cursor: 'pointer',
                    boxShadow: '0 20px 60px rgba(99, 102, 241, 0.25)',
                    border: '2px solid rgba(99, 102, 241, 0.2)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 25px 70px rgba(99, 102, 241, 0.35)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(99, 102, 241, 0.25)';
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #6366f1)',
                    animation: 'gradientShift 3s ease infinite'
                  }}></div>

                  <div style={{
                    fontSize: isMobile ? 14 : 16,
                    color: '#6366f1',
                    marginBottom: isMobile ? 20 : 30,
                    fontWeight: 'bold',
                    opacity: 0.8
                  }}>
                    بطاقة {currentIndex + 1} من {navTotal}
                  </div>

                  <div style={{
                    fontSize: isMobile ? 32 : 48,
                    fontWeight: 'bold',
                    color: '#4338ca',
                    marginBottom: isMobile ? 30 : 50,
                    textAlign: 'center',
                    lineHeight: 1.4,
                    direction: 'ltr'
                  }}>
                    {sortedQuestions[currentIndex].sentence || sortedQuestions[currentIndex].question}
                  </div>

                  <div style={{
                    fontSize: isMobile ? 15 : 18,
                    color: '#6366f1',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: 'rgba(99, 102, 241, 0.1)',
                    padding: '12px 24px',
                    borderRadius: 12,
                    border: '1px solid rgba(99, 102, 241, 0.2)'
                  }}>
                    <span style={{ fontSize: 20 }}>👇</span>
                    اضغط للترجمة
                  </div>
                </div>
              ) : (
                // Back of flashcard - show translation and everything else
                <div>
                  <div style={{
                    textAlign: 'center',
                    marginBottom: 20,
                    padding: '20px',
                    background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                    borderRadius: 16,
                    border: '2px solid rgba(99, 102, 241, 0.2)',
                    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.2)'
                  }}>
                    <div style={{
                      fontSize: isMobile ? 12 : 14,
                      color: '#6366f1',
                      marginBottom: 12,
                      fontWeight: 'bold'
                    }}>
                      بطاقة {currentIndex + 1} من {navTotal}
                    </div>
                    <div style={{
                      fontSize: isMobile ? 24 : 32,
                      fontWeight: 'bold',
                      color: '#4338ca',
                      marginBottom: 12,
                      direction: 'ltr'
                    }}>
                      {sortedQuestions[currentIndex].sentence || sortedQuestions[currentIndex].question}
                    </div>
                    <div style={{
                      fontSize: isMobile ? 20 : 28,
                      color: '#059669',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 12,
                      marginTop: 16
                    }}>
                      <span style={{ fontSize: isMobile ? 24 : 32 }}>↓</span>
                      <span>{sortedQuestions[currentIndex].correct}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Feedback Section and Action Buttons will appear below for flashcard mode */}
            </div>
          ) : (
            <div>
              {/* Regular Mode - Question text */}
              <div style={{ fontWeight: 'bold', fontSize: isMobile ? 18 : 22, marginBottom: 12, direction: 'ltr', textAlign: 'left' }}>
                {sortedQuestions[currentIndex].question || sortedQuestions[currentIndex].sentence || sortedQuestions[currentIndex].text || ''}
              </div>
              {/* Choices */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                

                {shuffledChoices.map((choice, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(choice)}
                    disabled={showFeedback}
                    style={{
                      background: selectedAnswer === choice 
                        ? (choice === sortedQuestions[currentIndex].correct 
                          ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                          : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)')
                        : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                      color: selectedAnswer === choice ? '#fff' : '#333',
                      border: selectedAnswer === choice 
                        ? '2px solid transparent'
                        : '1px solid rgba(0,0,0,0.1)',
                      borderRadius: 12,
                      padding: isMobile ? '0.8rem 1.2rem' : '1rem 1.8rem',
                      fontWeight: 'bold',
                      fontSize: isMobile ? 15 : 16,
                      cursor: showFeedback ? 'not-allowed' : 'pointer',
                      boxShadow: selectedAnswer === choice 
                        ? (choice === sortedQuestions[currentIndex].correct 
                          ? '0 8px 32px rgba(34, 197, 94, 0.3)'
                          : '0 8px 32px rgba(239, 68, 68, 0.3)')
                        : '0 4px 16px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      transform: selectedAnswer === choice ? 'translateY(-2px)' : 'translateY(0)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      if (!showFeedback && selectedAnswer !== choice) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!showFeedback && selectedAnswer !== choice) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                      }
                    }}
                  >
                    {selectedAnswer === choice && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: choice === sortedQuestions[currentIndex].correct 
                          ? 'linear-gradient(90deg, #22c55e, #16a34a, #15803d)'
                          : 'linear-gradient(90deg, #ef4444, #dc2626, #b91c1c)',
                        animation: 'gradientShift 2s ease infinite'
                      }}></div>
                    )}
                    {choice}
                  </button>
                ))}
              </div>
              
              {/* Admin: change level control */}
              {isAdmin && sortedQuestions[currentIndex] && (
                <div style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  marginBottom: 12
                }}>
                  <select
                    value={sortedQuestions[currentIndex].level}
                    onChange={async (e) => {
                      const newLevel = e.target.value;
                      const qid = sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString();
                      await fetch(process.env.REACT_APP_API_URL + `/api/admin/practice-questions/${qid}/level`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ level: newLevel })
                      });
                      // Refresh the current list to reflect new level
                      setLoading(true);
                      fetch(process.env.REACT_APP_API_URL + `/api/practice-questions?type=${selectedCategory}&level=${selectedLevel}`, { credentials: 'include' })
                        .then(res => res.json())
                        .then(data => {
                          setQuestions(data.questions || []);
                          setTotalCount(data.totalCount || 0);
                          setCurrentIndex(0);
                          setSelectedAnswer(null);
                          setShowFeedback(false);
                        })
                        .finally(() => setLoading(false));
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid rgba(0,0,0,0.15)'
                    }}
                  >
                    {levels.map(l => (
                      <option key={l.key} value={l.key}>{l.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Feedback Section - Shared between both modes */}
          {!loading && !error && sortedQuestions.length > 0 && (showFeedback || (flashcardMode && showFlashcardAnswer)) && (
            <div style={{ marginTop: 20 }}>
                  {/* Main Feedback - Only show in regular mode */}
                  {showFeedback && !flashcardMode && (
                    <div
                      ref={feedbackRef}
                      style={{
                        marginBottom: 16,
                        padding: '16px 20px',
                        borderRadius: 12,
                        background: selectedAnswer === sortedQuestions[currentIndex].correct
                          ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
                          : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                        border: selectedAnswer === sortedQuestions[currentIndex].correct
                          ? '1px solid rgba(34, 197, 94, 0.2)'
                          : '1px solid rgba(239, 68, 68, 0.2)',
                        color: selectedAnswer === sortedQuestions[currentIndex].correct ? '#166534' : '#991b1b',
                        fontWeight: 'bold',
                        fontSize: isMobile ? 15 : 16,
                        boxShadow: selectedAnswer === sortedQuestions[currentIndex].correct
                          ? '0 8px 32px rgba(34, 197, 94, 0.15)'
                          : '0 8px 32px rgba(239, 68, 68, 0.15)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {selectedAnswer === sortedQuestions[currentIndex].correct && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '2px',
                          background: 'linear-gradient(90deg, #22c55e, #16a34a, #15803d)',
                          animation: 'gradientShift 2s ease infinite'
                        }}></div>
                      )}
                      {selectedAnswer === sortedQuestions[currentIndex].correct ? '✅ إجابة صحيحة!' : `❌ إجابة خاطئة. الإجابة الصحيحة: ${sortedQuestions[currentIndex].correct}`}
                    </div>
                  )}
                  
                  {/* Explanation */}
                  {sortedQuestions[currentIndex].explanation && (
                    <div style={{ 
                      marginBottom: 16, 
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
                      color: '#92400e', 
                      borderRadius: 12, 
                      padding: '16px 20px', 
                      fontSize: 15, 
                      fontWeight: 'bold',
                      border: '1px solid rgba(245, 158, 11, 0.2)',
                      boxShadow: '0 8px 32px rgba(245, 158, 11, 0.15)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)',
                        animation: 'gradientShift 3s ease infinite'
                      }}></div>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 18, marginLeft: 8 }}>💡</span>
                        <span>الشرح:</span>
                      </div>
                      {sortedQuestions[currentIndex].explanation}
                    </div>
                  )}
                  
                  {/* Vocabulary */}
                  {sortedQuestions[currentIndex].vocab && sortedQuestions[currentIndex].vocab.length > 0 && (
                    <div style={{
                      background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                      borderRadius: 12,
                      padding: '16px 20px',
                      border: '1px solid rgba(99, 102, 241, 0.2)',
                      boxShadow: '0 8px 32px rgba(99, 102, 241, 0.15)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: 'linear-gradient(90deg, #6366f1, #4f46e5, #4338ca)',
                        animation: 'gradientShift 3s ease infinite'
                      }}></div>

                      {/* Header with toggle button */}
                      <div style={{
                        fontWeight: 'bold',
                        fontSize: 16,
                        marginBottom: 12,
                        color: '#3730a3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontSize: 18, marginLeft: 8 }}>📚</span>
                          الكلمات المهمة في السؤال:
                        </div>

                        {/* Mode Toggle */}
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <button
                            onClick={() => {
                              setVocabDisplayMode('list');
                              setCurrentVocabCardIndex(0);
                              setShowVocabCardAnswer(false);
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 8,
                              border: vocabDisplayMode === 'list' ? '2px solid #6366f1' : '1px solid rgba(99, 102, 241, 0.3)',
                              background: vocabDisplayMode === 'list'
                                ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
                                : 'rgba(255, 255, 255, 0.7)',
                              color: vocabDisplayMode === 'list' ? '#fff' : '#6366f1',
                              fontWeight: 'bold',
                              fontSize: 13,
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              boxShadow: vocabDisplayMode === 'list' ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none'
                            }}
                          >
                            📋 قائمة
                          </button>
                          <button
                            onClick={() => {
                              setVocabDisplayMode('cards');
                              setCurrentVocabCardIndex(0);
                              setShowVocabCardAnswer(false);
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 8,
                              border: vocabDisplayMode === 'cards' ? '2px solid #06b6d4' : '1px solid rgba(6, 182, 212, 0.3)',
                              background: vocabDisplayMode === 'cards'
                                ? 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)'
                                : 'rgba(255, 255, 255, 0.7)',
                              color: vocabDisplayMode === 'cards' ? '#fff' : '#06b6d4',
                              fontWeight: 'bold',
                              fontSize: 13,
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              boxShadow: vocabDisplayMode === 'cards' ? '0 4px 12px rgba(6, 182, 212, 0.3)' : 'none'
                            }}
                          >
                            🎴 بطاقات
                          </button>
                        </div>
                      </div>

                      {/* List Mode */}
                      {vocabDisplayMode === 'list' ? (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {sortedQuestions[currentIndex].vocab.map((v, i) => (
                            <li key={i} style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'flex-start',
                              alignItems: 'center',
                              marginBottom: 8,
                              padding: '8px 12px',
                              background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.6) 100%)',
                              borderRadius: 8,
                              border: '1px solid rgba(255,255,255,0.3)'
                            }}>
                              <span style={{
                                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                color: '#fff',
                                borderRadius: 8,
                                padding: '6px 12px',
                                marginLeft: 8,
                                fontWeight: 'bold',
                                fontSize: 15,
                                boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)'
                              }}>{v.word}</span>
                              <span style={{ color: '#4c1d95', fontSize: 15, fontWeight: '500' }}>{v.meaning}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        /* Cards Mode */
                        <div>
                          {!showVocabCardAnswer ? (
                            /* Front of card */
                            <div
                              onClick={() => setShowVocabCardAnswer(true)}
                              style={{
                                minHeight: isMobile ? 180 : 220,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                                borderRadius: 12,
                                padding: '2rem 1.5rem',
                                cursor: 'pointer',
                                border: '2px solid rgba(99, 102, 241, 0.2)',
                                transition: 'all 0.3s ease',
                                marginBottom: 12
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 12px 32px rgba(99, 102, 241, 0.25)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <div style={{
                                fontSize: isMobile ? 12 : 14,
                                color: '#6366f1',
                                marginBottom: isMobile ? 16 : 20,
                                fontWeight: 'bold'
                              }}>
                                كلمة {currentVocabCardIndex + 1} من {sortedQuestions[currentIndex].vocab.length}
                              </div>

                              <div style={{
                                fontSize: isMobile ? 28 : 36,
                                fontWeight: 'bold',
                                color: '#4338ca',
                                marginBottom: isMobile ? 20 : 30,
                                textAlign: 'center'
                              }}>
                                {sortedQuestions[currentIndex].vocab[currentVocabCardIndex].word}
                              </div>

                              <div style={{
                                fontSize: isMobile ? 13 : 15,
                                color: '#6366f1',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                background: 'rgba(99, 102, 241, 0.1)',
                                padding: '10px 20px',
                                borderRadius: 8
                              }}>
                                <span style={{ fontSize: 16 }}>👇</span>
                                اضغط للترجمة
                              </div>
                            </div>
                          ) : (
                            /* Back of card */
                            <div
                              style={{
                                minHeight: isMobile ? 180 : 220,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                                borderRadius: 12,
                                padding: '2rem 1.5rem',
                                border: '2px solid rgba(99, 102, 241, 0.2)',
                                marginBottom: 12
                              }}
                            >
                              <div style={{
                                fontSize: isMobile ? 12 : 14,
                                color: '#6366f1',
                                marginBottom: isMobile ? 12 : 16,
                                fontWeight: 'bold'
                              }}>
                                كلمة {currentVocabCardIndex + 1} من {sortedQuestions[currentIndex].vocab.length}
                              </div>

                              <div style={{
                                fontSize: isMobile ? 24 : 30,
                                fontWeight: 'bold',
                                color: '#4338ca',
                                marginBottom: 8
                              }}>
                                {sortedQuestions[currentIndex].vocab[currentVocabCardIndex].word}
                              </div>

                              <div style={{
                                fontSize: isMobile ? 18 : 24,
                                color: '#059669',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}>
                                <span style={{ fontSize: isMobile ? 20 : 24 }}>↓</span>
                                {sortedQuestions[currentIndex].vocab[currentVocabCardIndex].meaning}
                              </div>
                            </div>
                          )}

                          {/* Navigation buttons */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <button
                              onClick={() => {
                                if (currentVocabCardIndex > 0) {
                                  setCurrentVocabCardIndex(currentVocabCardIndex - 1);
                                  setShowVocabCardAnswer(false);
                                }
                              }}
                              disabled={currentVocabCardIndex === 0}
                              style={{
                                flex: 1,
                                padding: '10px 20px',
                                borderRadius: 10,
                                border: 'none',
                                background: currentVocabCardIndex === 0
                                  ? 'rgba(0,0,0,0.1)'
                                  : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                color: currentVocabCardIndex === 0 ? '#999' : '#fff',
                                fontWeight: 'bold',
                                fontSize: 15,
                                cursor: currentVocabCardIndex === 0 ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              → السابق
                            </button>
                            <button
                              onClick={() => {
                                if (currentVocabCardIndex < sortedQuestions[currentIndex].vocab.length - 1) {
                                  setCurrentVocabCardIndex(currentVocabCardIndex + 1);
                                  setShowVocabCardAnswer(false);
                                }
                              }}
                              disabled={currentVocabCardIndex === sortedQuestions[currentIndex].vocab.length - 1}
                              style={{
                                flex: 1,
                                padding: '10px 20px',
                                borderRadius: 10,
                                border: 'none',
                                background: currentVocabCardIndex === sortedQuestions[currentIndex].vocab.length - 1
                                  ? 'rgba(0,0,0,0.1)'
                                  : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                color: currentVocabCardIndex === sortedQuestions[currentIndex].vocab.length - 1 ? '#999' : '#fff',
                                fontWeight: 'bold',
                                fontSize: 15,
                                cursor: currentVocabCardIndex === sortedQuestions[currentIndex].vocab.length - 1 ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              ← التالي
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

          {/* Action buttons bar - Shared between both modes */}
          {!loading && !error && sortedQuestions.length > 0 && (
            <div>
              <div style={{ 
                marginTop: 20, 
                display: 'flex', 
                flexDirection: 'row-reverse', 
                alignItems: 'center', 
                justifyContent: 'flex-end', 
                gap: 16, 
                direction: 'rtl',
                flexWrap: 'wrap'
              }}>
                {/* Hint button */}
                {sortedQuestions[currentIndex].hint && (
                  <button 
                    onClick={() => setShowHint(h => !h)} 
                    title={showHint ? 'إخفاء التلميح' : 'عرض تلميح'}
                    style={{ 
                      background: showHint 
                        ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                        : 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                      border: '2px solid transparent',
                      color: showHint ? '#92400e' : '#fff',
                      fontWeight: 'bold', 
                      borderRadius: 12, 
                      padding: '12px 20px', 
                      fontSize: 15, 
                      cursor: 'pointer',
                      boxShadow: showHint 
                        ? '0 8px 32px rgba(245, 158, 11, 0.3)'
                        : '0 8px 32px rgba(251, 191, 36, 0.4)',
                      transition: 'all 0.3s ease',
                      transform: showHint ? 'translateY(-2px)' : 'translateY(0)',
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                    onMouseEnter={(e) => {
                      if (!showHint) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 12px 40px rgba(251, 191, 36, 0.5)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!showHint) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 8px 32px rgba(251, 191, 36, 0.4)';
                      }
                    }}
                  >
                    {showHint && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)',
                        animation: 'gradientShift 2s ease infinite'
                      }}></div>
                    )}
                                      <FaRegLightbulb style={{ 
                    fontSize: 20,
                    filter: showHint ? 'none' : 'brightness(1.2)'
                  }} />
                  </button>
                )}
                
                {/* Timer button */}
                {timerEnabled && (
                  <div
                    onClick={() => setShowTimer(t => !t)}
                    style={{
                      background: showTimer 
                        ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)'
                        : 'linear-gradient(135deg, rgba(14, 165, 233, 0.8) 0%, rgba(2, 132, 199, 0.8) 100%)',
                      color: '#fff',
                      borderRadius: 12,
                      padding: '12px 20px',
                      fontWeight: 'bold',
                      fontSize: 15,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      opacity: showTimer ? 1 : 0.8,
                      transition: 'all 0.3s ease',
                      boxShadow: showTimer 
                        ? '0 8px 32px rgba(14, 165, 233, 0.4)'
                        : '0 4px 16px rgba(14, 165, 233, 0.2)',
                      border: '2px solid transparent',
                      position: 'relative',
                      overflow: 'hidden',
                      minWidth: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 12px 40px rgba(14, 165, 233, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = showTimer 
                        ? '0 8px 32px rgba(14, 165, 233, 0.4)'
                        : '0 4px 16px rgba(14, 165, 233, 0.2)';
                    }}
                    title={showTimer ? 'إخفاء المؤقت' : 'عرض المؤقت'}
                  >
                    {showTimer && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: 'linear-gradient(90deg, #0ea5e9, #0284c7, #0369a1, #075985)',
                        animation: 'gradientShift 4s ease infinite'
                      }}></div>
                    )}
                    <FaRegClock style={{ 
                      marginLeft: 6,
                      fontSize: 18,
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))'
                    }} />
                    {showTimer && (
                      <span style={{
                        fontSize: 18,
                        fontWeight: 'bold',
                        fontFamily: 'monospace',
                        letterSpacing: '1px',
                        textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                      }}>
                        {String(Math.floor(timer / 60)).padStart(2, '0')}:{String(timer % 60).padStart(2, '0')}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Stats toggle button - Hidden on mobile */}
                {user && !isMobile && (
                  <button 
                    onClick={() => setShowStats(s => !s)} 
                    style={{ 
                      background: showStats 
                        ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                        : 'linear-gradient(135deg, rgba(139, 92, 246, 0.8) 0%, rgba(124, 58, 237, 0.8) 100%)',
                      border: 'none', 
                      color: '#fff', 
                      fontWeight: 'bold', 
                      borderRadius: 12, 
                      padding: '12px 20px', 
                      fontSize: 15, 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 8,
                      boxShadow: showStats 
                        ? '0 8px 32px rgba(139, 92, 246, 0.4)'
                        : '0 4px 16px rgba(139, 92, 246, 0.2)',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 12px 40px rgba(139, 92, 246, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = showStats 
                        ? '0 8px 32px rgba(139, 92, 246, 0.4)'
                        : '0 4px 16px rgba(139, 92, 246, 0.2)';
                    }}
                  >
                    {showStats && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: 'linear-gradient(90deg, #8b5cf6, #7c3aed, #6d28d9, #5b21b6)',
                        animation: 'gradientShift 4s ease infinite'
                      }}></div>
                    )}
                    <FaRegStickyNote style={{ 
                      marginLeft: 6,
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))'
                    }} /> 
                    {showStats ? 'إخفاء الإحصائيات' : 'عرض الإحصائيات'}
                  </button>
                )}
                
                {/* Favorite button */}
                {user && (
                  <button 
                    onClick={toggleFavorite} 
                    title={isFavorite ? 'إزالة من المفضلة' : 'إضافة للمفضلة'}
                    style={{ 
                      background: isFavorite 
                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                        : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                      border: isFavorite ? '2px solid transparent' : '1px solid rgba(0,0,0,0.1)',
                      color: isFavorite ? '#fff' : '#666',
                      borderRadius: 12,
                      padding: '12px 20px',
                      fontSize: 15,
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      boxShadow: isFavorite 
                        ? '0 8px 32px rgba(239, 68, 68, 0.4)'
                        : '0 4px 16px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      transform: isFavorite ? 'translateY(-2px)' : 'translateY(0)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      if (isFavorite) {
                        e.target.style.transform = 'translateY(-3px)';
                        e.target.style.boxShadow = '0 12px 40px rgba(239, 68, 68, 0.5)';
                      } else {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 6px 24px rgba(0,0,0,0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isFavorite) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 8px 32px rgba(239, 68, 68, 0.4)';
                      } else {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                      }
                    }}
                  >
                    {isFavorite && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: 'linear-gradient(90deg, #ef4444, #dc2626, #b91c1c, #991b1b)',
                        animation: 'gradientShift 4s ease infinite'
                      }}></div>
                    )}
                                          {isFavorite ? (
                        <FaHeart style={{ 
                          fontSize: 20,
                          filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))'
                        }} />
                      ) : (
                        <FaRegHeart style={{ 
                          fontSize: 20,
                          color: '#666'
                        }} />
                      )}
                  </button>
                )}
                
                {/* Share button */}
                <button 
                  onClick={shareQuestion} 
                  title="مشاركة السؤال"
                  style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: '2px solid transparent',
                    color: '#fff',
                    borderRadius: 12,
                    padding: '12px 20px',
                    fontSize: 15,
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
                    transition: 'all 0.3s ease',
                    transform: 'translateY(-2px)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-3px)';
                    e.target.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.4)';
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, #667eea, #764ba2, #f093fb, #667eea)',
                    animation: 'gradientShift 4s ease infinite'
                  }}></div>
                  <FaShareAlt style={{ 
                    fontSize: 18,
                    filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))'
                  }} />
                  مشاركة
                </button>
                
                {/* View Question Link button */}
                <button 
                  onClick={viewQuestion} 
                  title="عرض السؤال في صفحة منفصلة"
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    color: '#667eea',
                    borderRadius: 12,
                    padding: '12px 20px',
                    fontSize: 15,
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 6px 24px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                  }}
                >
                  <FaLink style={{ 
                    fontSize: 18
                  }} />
                  رابط
                </button>
              </div>
              
              {/* Hint Display - Moved above stats */}
              {sortedQuestions[currentIndex].hint && showHint && (
                <div style={{ 
                  marginTop: 20, 
                  marginBottom: 20,
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
                  color: '#92400e', 
                  borderRadius: 16, 
                  padding: '20px 24px', 
                  fontSize: 16,
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  boxShadow: '0 8px 32px rgba(245, 158, 11, 0.15)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)',
                    animation: 'gradientShift 3s ease infinite'
                  }}></div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontSize: 20, marginLeft: 8 }}>💡</span>
                    <span style={{ fontWeight: 'bold', fontSize: 18 }}>التلميح:</span>
                  </div>
                  <div style={{ lineHeight: 1.6, fontSize: 15 }}>
                    {sortedQuestions[currentIndex].hint}
                  </div>
                </div>
              )}
              
              {/* Enhanced Stats Display - Moved under action buttons */}
              {user && showStats && (
                <div style={{ 
                  marginTop: 20, 
                  padding: '20px 24px',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                  borderRadius: 16,
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  boxShadow: '0 8px 32px rgba(148, 163, 184, 0.15)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, #8b5cf6, #7c3aed, #6d28d9, #5b21b6)',
                    animation: 'gradientShift 4s ease infinite'
                  }}></div>
                  
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 16 
                  }}>
                    {/* Stats Header */}
                    <div style={{ 
                      textAlign: 'center',
                      marginBottom: 8
                    }}>
                      <span style={{ 
                        fontSize: 18, 
                        fontWeight: 'bold', 
                        color: '#1e293b',
                        textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}>
                        📊 إحصائيات السؤال
                      </span>
                    </div>
                    
                    {/* Stats Cards */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 16,
                      flexWrap: 'wrap'
                    }}>
                      {/* Correct Answers Card */}
                      <div style={{ 
                        flex: 1,
                        minWidth: '120px',
                        padding: '16px 20px',
                        background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                        borderRadius: 12,
                        border: '1px solid rgba(34, 197, 94, 0.2)',
                        boxShadow: '0 4px 16px rgba(34, 197, 94, 0.15)',
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '2px',
                          background: 'linear-gradient(90deg, #22c55e, #16a34a, #15803d)',
                          animation: 'gradientShift 3s ease infinite'
                        }}></div>
                        <div style={{ 
                          fontSize: 24, 
                          fontWeight: 'bold', 
                          color: '#15803d',
                          marginBottom: 4
                        }}>
                          {getStatsForQuestion(sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString()).correct || 0}
                        </div>
                        <div style={{ 
                          fontSize: 14, 
                          color: '#166534',
                          fontWeight: '500'
                        }}>
                          إجابات صحيحة
                        </div>
                      </div>
                      
                      {/* Wrong Answers Card */}
                      <div style={{ 
                        flex: 1,
                        minWidth: '120px',
                        padding: '16px 20px',
                        background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                        borderRadius: 12,
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        boxShadow: '0 4px 16px rgba(239, 68, 68, 0.15)',
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '2px',
                          background: 'linear-gradient(90deg, #ef4444, #dc2626, #b91c1c)',
                          animation: 'gradientShift 3s ease infinite'
                        }}></div>
                        <div style={{ 
                          fontSize: 24, 
                          fontWeight: 'bold', 
                          color: '#b91c1c',
                          marginBottom: 4
                        }}>
                          {getStatsForQuestion(sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString()).wrong || 0}
                        </div>
                        <div style={{ 
                          fontSize: 14, 
                          color: '#991b1b',
                          fontWeight: '500'
                        }}>
                          إجابات خاطئة
                        </div>
                      </div>
                      
                      {/* Success Rate Card */}
                      <div style={{ 
                        flex: 1,
                        minWidth: '120px',
                        padding: '16px 20px',
                        background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                        borderRadius: 12,
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        boxShadow: '0 4px 16px rgba(59, 130, 246, 0.15)',
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '2px',
                          background: 'linear-gradient(90deg, #3b82f6, #2563eb, #1d4ed8)',
                          animation: 'gradientShift 3s ease infinite'
                        }}></div>
                        <div style={{ 
                          fontSize: 24, 
                          fontWeight: 'bold', 
                          color: '#1d4ed8',
                          marginBottom: 4
                        }}>
                          {(() => {
                            const stats = getStatsForQuestion(sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString());
                            const total = (stats.correct || 0) + (stats.wrong || 0);
                            return total > 0 ? Math.round(((stats.correct || 0) / total) * 100) : 0;
                          })()}%
                        </div>
                        <div style={{ 
                          fontSize: 14, 
                          color: '#1e40af',
                          fontWeight: '500'
                        }}>
                          نسبة النجاح
                        </div>
                      </div>
                    </div>
                    
                    {/* Last Attempt Info */}
                    {getStatsForQuestion(sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString()).lastAttempt && (
                      <div style={{ 
                        padding: '12px 16px',
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.6) 100%)',
                        borderRadius: 10,
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        textAlign: 'center'
                      }}>
                        <span style={{ 
                          fontSize: 14, 
                          color: '#475569',
                          fontWeight: '500'
                        }}>
                          🕒 آخر محاولة: {timeAgo(getStatsForQuestion(sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString()).lastAttempt)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              


              {/* Notes section */}
              {user && (
                <div style={{ 
                  marginTop: 20, 
                  background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', 
                  borderRadius: 16, 
                  padding: '20px 24px',
                  border: '1px solid rgba(14, 165, 233, 0.2)',
                  boxShadow: '0 8px 32px rgba(14, 165, 233, 0.15)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, #0ea5e9, #0284c7, #0369a1, #075985)',
                    animation: 'gradientShift 4s ease infinite'
                  }}></div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: 12 
                  }}>
                    <FaRegStickyNote style={{ 
                      marginLeft: 8, 
                      color: '#0369a1',
                      fontSize: 20
                    }} />
                    <span style={{ 
                      fontWeight: 'bold', 
                      fontSize: 16,
                      color: '#0369a1'
                    }}>ملاحظاتك على هذا السؤال:</span>
                  </div>
                  {noteEditMode ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <textarea
                        value={noteDraft}
                        onChange={e => setNoteDraft(e.target.value)}
                        rows={3}
                        placeholder="اكتب ملاحظاتك هنا..."
                        style={{ 
                          borderRadius: 12, 
                          border: '2px solid rgba(14, 165, 233, 0.3)', 
                          padding: '12px 16px', 
                          fontSize: 15,
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                          boxShadow: '0 4px 16px rgba(14, 165, 233, 0.1)',
                          transition: 'all 0.3s ease',
                          resize: 'vertical',
                          minHeight: '80px'
                        }}
                        onFocus={(e) => {
                          e.target.style.border = '2px solid rgba(14, 165, 233, 0.6)';
                          e.target.style.boxShadow = '0 8px 32px rgba(14, 165, 233, 0.2)';
                        }}
                        onBlur={(e) => {
                          e.target.style.border = '2px solid rgba(14, 165, 233, 0.3)';
                          e.target.style.boxShadow = '0 4px 16px rgba(14, 165, 233, 0.1)';
                        }}
                      />
                      <div style={{ display: 'flex', gap: 12 }}>
                        <button 
                          onClick={() => { saveNote(sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString(), noteDraft); }} 
                          style={{ 
                            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                            color: '#fff', 
                            border: 'none', 
                            borderRadius: 12, 
                            padding: '10px 20px', 
                            fontWeight: 'bold', 
                            fontSize: 15, 
                            cursor: 'pointer',
                            boxShadow: '0 8px 32px rgba(34, 197, 94, 0.3)',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 12px 40px rgba(34, 197, 94, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 8px 32px rgba(34, 197, 94, 0.3)';
                          }}
                        >
                          <FaSave style={{ marginLeft: 4 }} />
                          حفظ
                        </button>
                        <button 
                          onClick={() => setNoteEditMode(false)} 
                          style={{ 
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: '#fff', 
                            border: 'none', 
                            borderRadius: 12, 
                            padding: '10px 20px', 
                            fontWeight: 'bold', 
                            fontSize: 15, 
                            cursor: 'pointer',
                            boxShadow: '0 8px 32px rgba(239, 68, 68, 0.3)',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 12px 40px rgba(239, 68, 68, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 8px 32px rgba(239, 68, 68, 0.3)';
                          }}
                        >
                          <FaTimes style={{ marginLeft: 4 }} />
                          إلغاء
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 12,
                      flexWrap: 'wrap'
                    }}>
                      <div style={{
                        flex: 1,
                        minWidth: '200px',
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.6) 100%)',
                        padding: '12px 16px',
                        borderRadius: 12,
                        border: '1px solid rgba(255,255,255,0.3)',
                        fontSize: 15,
                        color: '#1e293b'
                      }}>
                        {getNoteForQuestion(sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString())?.text || 'لا يوجد ملاحظات بعد.'}
                      </div>
                      <button 
                        onClick={() => {
                          setNoteEditMode(true);
                          setNoteDraft(getNoteForQuestion(sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString())?.text || '');
                        }} 
                        style={{ 
                          background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                          color: '#fff', 
                          border: 'none', 
                          borderRadius: 12, 
                          padding: '8px 16px', 
                          fontWeight: 'bold', 
                          fontSize: 15, 
                          cursor: 'pointer',
                          boxShadow: '0 8px 32px rgba(14, 165, 233, 0.3)',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 12px 40px rgba(14, 165, 233, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 8px 32px rgba(14, 165, 233, 0.3)';
                        }}
                      >
                        <FaEdit style={{ marginLeft: 4 }} />
                        تعديل
                      </button>
                      {getNoteForQuestion(sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString()) && (
                        <button 
                          onClick={() => deleteNote(sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString())} 
                          style={{ 
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: '#fff', 
                            border: 'none', 
                            borderRadius: 12, 
                            padding: '8px 16px', 
                            fontWeight: 'bold', 
                            fontSize: 15, 
                            cursor: 'pointer',
                            boxShadow: '0 8px 32px rgba(239, 68, 68, 0.3)',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 12px 40px rgba(239, 68, 68, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 8px 32px rgba(239, 68, 68, 0.3)';
                          }}
                        >
                          <FaTrash style={{ marginLeft: 4 }} />
                          حذف
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {/* In the main question rendering, add a delete button for admin users: */}
              {user && user.role === 'admin' && (
                <button
                  onClick={() => handleDeleteQuestion(sortedQuestions[currentIndex]._id && sortedQuestions[currentIndex]._id.toString())}
                  style={{ background: '#fff', color: '#e11d48', border: '1px solid #e11d48', borderRadius: 6, padding: '4px 14px', fontWeight: 'bold', fontSize: 15, cursor: 'pointer', marginBottom: 10, marginRight: 8 }}
                >
                  حذف السؤال
                </button>
              )}
              {/* Navigation */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
                <button 
                  onClick={handlePrev} 
                  disabled={currentIndex === 0} 
                  style={{ 
                    padding: '10px 20px', 
                    borderRadius: 12, 
                    border: currentIndex === 0 ? '1px solid rgba(0,0,0,0.1)' : '2px solid transparent',
                    background: currentIndex === 0 
                      ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.3) 100%)'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: currentIndex === 0 ? '#999' : '#fff',
                    fontWeight: 'bold', 
                    fontSize: 16, 
                    cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                    boxShadow: currentIndex === 0 
                      ? '0 4px 16px rgba(0,0,0,0.1)'
                      : '0 8px 32px rgba(102, 126, 234, 0.3)',
                    transition: 'all 0.3s ease',
                    transform: currentIndex === 0 ? 'translateY(0)' : 'translateY(0)'
                  }}
                  onMouseEnter={(e) => {
                    if (currentIndex !== 0) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentIndex !== 0) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.3)';
                    }
                  }}
                >
                  السابق
                </button>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  padding: '10px 20px',
                  borderRadius: 12,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                }}>
                  <span style={{ 
                    fontWeight: 'bold', 
                    fontSize: 16
                  }}>
                    السؤال {currentIndex + 1} من {navTotal}
                  </span>
                  
                  {/* Quick Navigation - only show when no filters/ordering */}
                  {shouldShowNavigation && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {!showJumpInput ? (
                        <button
                          onClick={() => setShowJumpInput(true)}
                          style={{
                            background: 'rgba(102, 126, 234, 0.1)',
                            border: '1px solid rgba(102, 126, 234, 0.3)',
                            borderRadius: 6,
                            padding: '4px 8px',
                            fontSize: 12,
                            color: '#667eea',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                          title="انتقل لرقم سؤال محدد"
                        >
                          🔢
                        </button>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="number"
                            value={jumpToQuestion}
                            onChange={(e) => setJumpToQuestion(e.target.value)}
                            onKeyDown={handleJumpKeyPress}
                            placeholder="رقم"
                            min="1"
                            max={sortedQuestions.length}
                            style={{
                              width: 50,
                              padding: '4px 6px',
                              border: '1px solid #ddd',
                              borderRadius: 4,
                              fontSize: 12,
                              textAlign: 'center'
                            }}
                            autoFocus
                          />
                          <button
                            onClick={handleJumpToQuestion}
                            style={{
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              padding: '4px 8px',
                              fontSize: 11,
                              cursor: 'pointer'
                            }}
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => {
                              setShowJumpInput(false);
                              setJumpToQuestion('');
                            }}
                            style={{
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              padding: '4px 8px',
                              fontSize: 11,
                              cursor: 'pointer'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleNextPremium}
                  disabled={user && (user.premium || user.role === 'admin') ? currentIndex === sortedQuestions.length - 1 : false}
                  style={{ 
                    padding: '10px 20px', 
                    borderRadius: 12, 
                    border: (user && (user.premium || user.role === 'admin') && currentIndex === sortedQuestions.length - 1) ? '1px solid rgba(0,0,0,0.1)' : '2px solid transparent',
                    background: (user && (user.premium || user.role === 'admin') && currentIndex === sortedQuestions.length - 1)
                      ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.3) 100%)'
                      : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: (user && (user.premium || user.role === 'admin') && currentIndex === sortedQuestions.length - 1) ? '#999' : '#fff',
                    fontWeight: 'bold', 
                    fontSize: 16, 
                    cursor: (user && (user.premium || user.role === 'admin') && currentIndex === sortedQuestions.length - 1) ? 'not-allowed' : 'pointer',
                    boxShadow: (user && (user.premium || user.role === 'admin') && currentIndex === sortedQuestions.length - 1)
                      ? '0 4px 16px rgba(0,0,0,0.1)'
                      : '0 8px 32px rgba(34, 197, 94, 0.3)',
                    transition: 'all 0.3s ease',
                    transform: (user && (user.premium || user.role === 'admin') && currentIndex === sortedQuestions.length - 1) ? 'translateY(0)' : 'translateY(0)'
                  }}
                  onMouseEnter={(e) => {
                    if (!(user && (user.premium || user.role === 'admin') && currentIndex === sortedQuestions.length - 1)) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 12px 40px rgba(34, 197, 94, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(user && (user.premium || user.role === 'admin') && currentIndex === sortedQuestions.length - 1)) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 8px 32px rgba(34, 197, 94, 0.3)';
                    }
                  }}
                >
                  التالي
                </button>
              </div>
            </div>
          )}
          {/* FULL FEATURED QUESTION RENDERING SECTION END */}
        </CardContent>
      </Card>
    </div>
    </>
  );
}